import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PhoneIcon from '@material-ui/icons/Phone'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  addToPipeline,
  extractParentSignals,
  fetchEnrollmentDirectory,
  fetchPendingChats,
  fetchInteractionsNotLogged,
  fetchDailyConnected,
  fetchFocus,
  fetchCockpit,
  fetchCentreScoreTrends,
  fetchMyDay,
  fetchRevenueGoalSummary,
  removeFromPipeline
} from 'api'
import { showToast } from 'app/helpers'

import InteractionDrawer from './InteractionDrawer'
import GuideButton from './GuideButton'
import ReportsPanel from './ReportsPanel'
import { currentMonth, dayMonthYear, inr, monthLabel, monthOptions } from './monthHelpers'

// MY DAY (user decision 2026-07-21) — replaces My Centre.
//
// The complaint that drove it: the team had sixteen possible screens and none
// of them answered "who do I call today?". So this is ONE page — a short,
// finishable list, ordered by rupees, with the reason and the action on the
// row itself. Everything analytical moved behind the Afterschool Funnel tab.
//
// Lane order is priority and comes from the backend (lane_order) — it is not a
// user choice. Healthy and Dormant are deliberately counts, never lists.

const LANE_COLOR = {
  active: '#2E7D32',
  at_risk: '#C62828',
  grow: '#1565C0',
  winback: '#8E24AA',
  churned: '#6a6a6a',
  // Paused chip (user 2026-08-02): amber for a recoverable pause.
  paused: '#B26A00',
  pipeline: '#0E8A8A',
  acquire: '#0E8A8A'
}

// The programme-category buttons (user screenshot 2026-07-21). Labels and
// order are fixed; counts come from the backend.
const CATEGORY_LABEL = {
  all: 'All',
  preschool: 'Preschool',
  afterschool: 'Afterschool',
  daycare: 'Daycare',
  toddler_club: 'Toddler Club',
  other: 'Other'
}
const CATEGORY_ORDER = ['all', 'preschool', 'afterschool', 'daycare', 'toddler_club', 'other']
// Pale per-category chip colours for the Pipeline list (user 2026-08-07 —
// tentative month + categories interested in, shown as chips).
const CAT_CHIP = {
  preschool: { background: '#eaf2ff', color: '#1565C0' },
  afterschool: { background: '#f3eafc', color: '#8E24AA' },
  daycare: { background: '#e9f6f2', color: '#0E8A8A' },
  toddler_club: { background: '#fdeef6', color: '#B0338B' },
  other: { background: '#f2f5f9', color: '#48586a' }
}
// LOCAL calendar date (not UTC). `toISOString()` shifts to UTC, so in IST the
// Daily Connected picker could default to yesterday and query an empty day.
const localDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
// Growth Toolkit §3.2: a tab per PROGRAMME, and only these three. Daycare and
// 'other' deliberately have no tab -- they stay searchable in All active
// enrolled and their count is stated under the tabs, so nothing is dropped
// silently (§1). CATEGORY_ORDER is still used by the follow-up and pipeline
// panels, which filter rather than tab.
const PROGRAMME_TABS = ['afterschool', 'preschool', 'toddler_club']
// Preschool has no Winback -- only a wallet holds money and preschool has none.
// Churned is afterschool-only for now (user 2026-07-30) — preschool/TC keep
// their lanes and don't offer a Churned tab.
const LANES_BY_PROGRAMME = {
  // Mandate Cancelled sits after Churned (user 2026-08-09, source = revoked
  // e-mandate tokens). Scheduled-to-unsubscribe folds into At Risk, so it has
  // no chip of its own.
  // New enquiry (member-app New Program Enquiry) sits after Grow in every
  // programme (user 2026-08-09), routed by the programme asked about.
  // Paused moved to Reports (user 2026-09-24) so the checks sit in one place.
  // The backend still builds the lane — it keeps paused families out of
  // Winback / Churned — it just isn't a tab any more.
  afterschool: ['active', 'at_risk', 'grow', 'program_enquiry', 'winback', 'churned', 'mandate_cancelled'],
  preschool: ['active', 'at_risk', 'grow', 'program_enquiry', 'pipeline'],
  toddler_club: ['active', 'at_risk', 'winback', 'grow', 'program_enquiry']
}

// At Risk sub-filters: [row flag, chip label] (user 2026-07-29). A chip only
// shows when the current lane has rows carrying that flag, so preschool/TC
// (which don't set these flags) never show an empty filter bar.
const AT_RISK_FILTERS = [
  ['recharge_due', 'Needs recharge'],
  ['discovery_ending', 'Discovery ending'],
  ['low_attendance', 'Barely attending'],
  ['interaction_negative', 'Unhappy'],
  // Parents who tapped "unsubscribe" in the app (open Unsubscribe ticket) —
  // folded into At Risk with its own chip (user 2026-08-09).
  ['requested_unsubscribe', 'Requested to unsubscribe']
]

// Winback chips (user 2026-07-30). Default = all inactive; these narrow it.
const MS_MONTH = 1000 * 60 * 60 * 24 * 30
const WB_LAPSED = [['all', 'All'], ['3mo', 'Last 3 mo'], ['3-6', '3–6 mo'], ['6+', '6+ mo']]
const WB_COINS = [['all', 'All'], ['0', '0'], ['<3k', '< 3,000'], ['3k+', '3,000+']]
const WB_EXPIRY = [['all', 'All'], ['3mo', 'Next 3 mo'], ['3-6', '3–6 mo'], ['6+', '6+ mo']]
const WB_SUBJECTS = ['Art & Design', 'Public Speaking', 'Chess', 'Music', 'Robotics', 'Extracurricular Club']
// months since lapse: last coin activity (afterschool) or the subscription end
// date (toddler club). Big number when never/unknown, so it lands in 6+.
const wbLapsedMonths = (r) => {
  const d = (r.detail_fields || {}).last_used || (r.detail_fields || {}).lapsed_on
  return d ? (Date.now() - new Date(d).getTime()) / MS_MONTH : 999
}
// months until coins expire (null when no expiry / already expired)
const wbExpiryMonths = (r) => {
  const ex = (r.detail_fields || {}).coins_expiry
  if (!ex) return null
  const m = (new Date(ex).getTime() - Date.now()) / MS_MONTH
  return m < 0 ? null : m
}
const wbCoinsOf = (r) =>
  r.coins_left != null ? r.coins_left : ((r.detail_fields || {}).wallet_balance || 0)
const wbBand = (dim, k, r) => {
  if (k === 'all') return true
  if (dim === 'lapsed') {
    const m = wbLapsedMonths(r)
    return k === '3mo' ? m <= 3 : k === '3-6' ? (m > 3 && m <= 6) : m > 6
  }
  if (dim === 'coins') {
    const c = wbCoinsOf(r)
    return k === '0' ? c === 0 : k === '<3k' ? (c > 0 && c < 3000) : c >= 3000
  }
  if (dim === 'expiry') {
    const m = wbExpiryMonths(r)
    if (m == null) return false
    return k === '3mo' ? m <= 3 : k === '3-6' ? (m > 3 && m <= 6) : m > 6
  }
  return true
}

// The parameters that actually decide the call, shown on the row itself — no
// clicking (user 2026-07-22). Order is priority: the first five that a row
// has are the ones that render, so a preschool row leads with how overdue it
// is and an afterschool row with how empty the wallet is.
//
// This list is deliberately short. Everything else the engines compute —
// batch, coverage window, student name, invoice id, next payment date,
// typical monthly spend — was tried in an expanded panel and read as filler.
const num = (v) => (v == null ? null : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 }))

const CHIPS = [
  // preschool / daycare: is it late, and can they even pay it
  ['overdue_days', 'overdue', (v) => (v > 0 ? `${v} days overdue` : null), () => 'bad'],
  ['invoice_status', 'link', (v) => ({ expired: 'link expired', pending: 'link unpaid' }[v]), () => 'bad'],
  // afterschool: has the money run out, and are they still turning up.
  // These four ARE the At Risk flags, so they lead — the working order (short
  // for next month / discovery ending / attendance) is only trustworthy if the
  // row shows the number that put it there. `shortfall_coins` is the
  // recharge-due flag, `attendance_pct` the low-attendance one.
  // Recharge due is ONE chip, worded the same on every row (Shruti,
  // 2026-07-24). It used to print the shortfall — "short 860 coins for next
  // month" — which made every row's leading chip read differently and, worse,
  // rendered NOTHING on a recharge-due row with no burn history to size the
  // shortfall from. The chip is driven by the FLAG now, not by whether a
  // number could be worked out; the coins left and the burn are still in the
  // interaction drawer for anyone who wants them.
  // 'Needs Recharge' is the platform's own wording — it is the CRM wallet flag
  // the team already reads on the parent profile (Shruti, 2026-07-24). Two
  // names for one condition is how a team stops trusting a page.
  ['recharge_due', 'recharge', (v) => (v ? 'needs recharge' : null), () => 'bad'],
  ['discovery_end_date', 'discovery', (v) => `discovery ends ${dayMonthYear(v)}`, () => 'bad'],
  ['attendance_pct', 'attendance', (v) => `attendance ${v}%`, (v) => (v < 50 ? 'bad' : null)],
  // An unmarked session counts as absent, so a register nobody filled in and a
  // child who stopped coming both read as 0%. Say which one this is — otherwise
  // the team stops trusting the attendance number altogether.
  ['attendance_unmarked', 'register', (v, d) => (v ? (d.unmarked_sessions ? `${d.unmarked_sessions} sessions not marked` : 'attendance not being marked') : null), () => 'warn'],
  ['churn_signal', 'churn', (v) => (v ? 'last note suggests churn' : null), () => 'bad'],
  // Coin counts and coin expiry are deliberately NOT here for At Risk (Shruti,
  // 2026-07-24): "needs recharge" is the decision, and the balance, the
  // shortfall and the expiry date are detail the caller does not need on the
  // line. `LANE_CHIP_SKIP` below keeps them on the lanes where they ARE the
  // decision — expiry is the entire ranking of Winback.
  ['wallet_balance', 'wallet', (v) => `${num(v)} coins left`, (v, d) => (d.coins_needed != null && v < d.coins_needed ? 'bad' : null)],
  ['coins_expiry', 'expiry', (v) => `expires ${dayMonthYear(v)}`, () => 'warn'],
  // a broken promise outranks almost anything else on a call
  ['promised_date', 'promised', (v, d) => (d.promise_overdue ? `promised ${dayMonthYear(v)} — overdue` : `promised ${dayMonthYear(v)}`), (v, d) => (d.promise_overdue ? 'bad' : 'warn')],
  ['contactability', 'reachable', (v) => ({ dnp: 'does not pick up' }[v]), () => 'bad'],
  // grow — new parents. The rating is a LABEL, not the sort (the list is
  // ordered by how recently they walked in), and "nobody has logged an
  // interaction" replaces the rating rather than sitting beside it: an unrated
  // parent hasn't been judged low, they haven't been spoken to.
  ['new_parent_tier', 'rating', (v) => ({
    not_logged: 'interaction not logged',
    High: 'high intent',
    Medium: 'medium intent',
    Low: 'low intent'
  }[v]), (v) => ({ not_logged: 'bad', High: 'ok', Medium: 'warn' }[v] || null)],
  ['visit_date', 'visited', (v) => `visited ${dayMonthYear(v)}`],
  // toddler club — a fixed-period subscription, so the risk questions are
  // about the period: is anything booked ahead, and when does it run out.
  ['sessions_booked_ahead', 'booked', (v) => (v > 0 ? `${v} sessions booked ahead` : 'nothing booked ahead'), (v) => (v > 0 ? null : 'bad')],
  ['subscription_end', 'ends', (v) => `subscription ends ${dayMonthYear(v)}`, () => 'warn'],
  // Winback: the subscription has already lapsed, so past tense (TC-only field,
  // so afterschool winback's coins chips are untouched).
  ['subscription_ended', 'ended', (v) => `subscription ended ${dayMonthYear(v)}`, () => 'warn'],
  ['paid_sessions', 'paid', (v) => (v > 0 ? `${v} sessions paid one at a time` : null), () => 'ok'],
  ['winback_tier', 'priority', (v) => `${v.toLowerCase()} priority`, (v) => (v === 'High' ? 'ok' : null)],
  // winback
  ['last_used', 'last seen', (v) => `last came ${dayMonthYear(v)}`],
  // Skipped on new-parent rows — new_parent_tier above already says this, and
  // the two would render the same words twice.
  ['intent', 'intent', (v, d) => (d.new_parent_tier ? null : `${v.toLowerCase()} intent`), (v) => (v === 'High' ? 'ok' : null)],
  ['enquired_programs', 'asked about', (v) => (Array.isArray(v) && v.length ? `asked about ${v.join(', ')}` : null)],
  ['tentative_joining_date', 'joining', (v) => `wants to join ${dayMonthYear(v)}`]
]

const MAX_CHIPS = 5

// Chips a given lane does not print. At Risk states the decision — "needs
// recharge" — and leaves the arithmetic behind it (how many coins are left,
// how many are short, when they expire) to the interaction drawer; on a call
// list it was three numbers in place of one instruction (Shruti, 2026-07-24).
// Winback is untouched: there the balance IS the reason to call and the expiry
// date IS the ranking.
const LANE_CHIP_SKIP = {
  // 'churn_signal' dropped 2026-07-28: "last note suggests churn" is a generic
  // restatement — the row now shows the ACTUAL note instead (see the reason
  // block below).
  at_risk: ['wallet_balance', 'coins_expiry', 'shortfall_coins', 'coins_needed', 'churn_signal'],
  // Winback shows the interaction status as its reason line (intent_label) and
  // the conversation summary underneath, so the 'promised' chip would just
  // repeat it (user 2026-07-28).
  winback: ['promised_date']
}

// The directory is the biggest query on the page — Indiranagar alone runs to
// thousands of enrolments. Render a slice and let search do the narrowing;
// dumping every row unpaginated is what made the old Full List slow.
const ENROLLED_CAP = 100

// Add-to-pipeline: the CATEGORY is now picked explicitly (user 2026-07-23)
// rather than inferred from whichever row the parent appeared on. Inferring it
// was wrong exactly when it mattered — a preschool family asking about
// afterschool is the case a pipeline exists to capture.
//
// Preschool/Daycare is a fixed ladder. Afterschool subjects come LIVE from the
// centre's catalogue (data.afterschool_subjects) because the catalogue changes
// every term; free text stays available on top, since the team is recording
// what a parent SAID and that is routinely not on the catalogue yet.
const GRADES = ['Toddler', 'Pre-Nursery', 'Nursery', 'K1', 'K2']
const GRADE_CATEGORIES = ['preschool', 'daycare']
const PIPELINE_CATEGORIES = ['preschool', 'afterschool', 'daycare', 'toddler_club', 'other']

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2, 3) },
  header: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  title: { fontWeight: 700 },
  // The category switcher. Lives in the header row's spare middle space, and
  // is deliberately quiet — it is a filter, not the subject of the page, so it
  // must not shout louder than the lane tabs or the money column.
  // Follow-ups / Pipeline (user 2026-07-23): black while closed, so neither
  // shouts; the OPEN one turns orange with white text, which is the page's only
  // "you are looking at this" signal. The red overdue outline was removed with
  // it — the count still says "97 overdue", and two competing colours on one
  // button made the open/closed state unreadable.
  panelBtn: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    color: '#1b2733',
    borderColor: '#c9d4e0'
  },
  panelBtnOn: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    background: theme.palette.primary.main,
    color: '#fff',
    '&:hover': { background: theme.palette.primary.main }
  },
  catBar: { display: 'flex', flexWrap: 'wrap' },
  catBtn: {
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: 13,
    fontWeight: 400,
    padding: '5px 13px',
    border: '1px solid #d9e1e9',
    borderRight: 'none',
    background: '#fff',
    color: '#5a6b7d',
    whiteSpace: 'nowrap',
    '&:first-child': { borderTopLeftRadius: 6, borderBottomLeftRadius: 6 },
    '&:last-child': { borderRight: '1px solid #d9e1e9', borderTopRightRadius: 6, borderBottomRightRadius: 6 },
    '&:hover': { background: '#f6f8fa' }
  },
  catBtnOn: { background: '#eef2f7', color: '#1b2a3a', fontWeight: 500 },
  catCount: { color: '#9aa7b3', marginLeft: 3 },
  // Lane tabs — one lane at a time so a long lane gets the full page and the
  // others can't be scrolled past (user 2026-07-21).
  laneTabs: { display: 'flex', flexWrap: 'wrap', gap: 6, margin: '2px 0 14px' },
  laneTab: {
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: 13,
    fontWeight: 500,
    padding: '7px 14px',
    borderRadius: 7,
    border: '1px solid #d9e1e9',
    background: '#fff',
    color: '#48586a',
    whiteSpace: 'nowrap',
    '&:hover': { background: '#f6f8fa' }
  },
  laneTabOn: { background: '#eef2f7', color: '#12203a', borderColor: '#c3d0dd', fontWeight: 600 },
  laneTabCount: { color: '#9aa7b3', marginLeft: 4, fontWeight: 400 },
  swatch: { width: 9, height: 9, borderRadius: 2, flex: 'none' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 12px',
    border: '1px solid #e4eaf1',
    borderRadius: 8,
    background: '#fff',
    marginBottom: 7
  },
  cat: { fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', padding: '3px 7px', borderRadius: 4, whiteSpace: 'nowrap' },
  who: { flex: 1, minWidth: 0 },
  name: { fontWeight: 700, fontSize: 14 },
  reason: { fontSize: 12, color: '#48586a', marginTop: 1 },
  last: { fontSize: 11, color: '#8493a5', marginTop: 1 },
  stake: { textAlign: 'right', whiteSpace: 'nowrap' },
  stakeAmt: { fontWeight: 700, fontSize: 14 },
  stakeCap: { fontSize: 10, color: '#8493a5', textTransform: 'uppercase', letterSpacing: 0.4 },
  phone: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#1565C0', textDecoration: 'none', fontSize: 13 },
  showAll: {
    textAlign: 'center',
    fontSize: 12,
    color: '#5a6b7d',
    border: '1px dashed #d9e1e9',
    borderRadius: 8,
    padding: 9,
    cursor: 'pointer',
    marginTop: 4
  },
  rowPhone: { color: '#48586a', fontWeight: 600, marginRight: 8 },
  uncovered: {
    fontSize: 11.5,
    color: '#8493a5',
    padding: '5px 2px 0',
  },
  lane: { marginBottom: 20 },
  pipelinePanel: {
    border: '1px solid #d9e1e9',
    borderRadius: 10,
    padding: '13px 15px',
    background: '#fbfcfe',
    marginBottom: 16
  },
  pipelineHead: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 4 },
  // Focus for the day — the layer over Grow & Retain. A scorecard header + the
  // four daily must-do targets as a prioritized worklist.
  focusScore: { display: 'flex', alignItems: 'baseline', gap: 8 },
  focusScoreNum: { fontSize: 30, fontWeight: 800, lineHeight: 1, color: '#12203a' },
  focusScoreOf: { fontSize: 15, color: '#8493a5', fontWeight: 600 },
  focusTarget: {
    border: '1px solid #e4eaf1',
    borderLeft: '4px solid #d9e1e9',
    borderRadius: 8,
    background: '#fff',
    padding: '11px 14px',
    marginBottom: 9
  },
  focusTargetHead: { display: 'flex', alignItems: 'center', gap: 10 },
  focusTargetTitle: { fontWeight: 700, fontSize: 14.5, flex: 1, minWidth: 0 },
  focusPill: { fontSize: 11, fontWeight: 800, letterSpacing: 0.3, padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap' },
  focusWhy: { fontSize: 12, color: '#6b7889', marginTop: 3 },
  focusLegs: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  focusLeg: { fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 6, border: '1px solid #e4eaf1' },
  // Stated, not implied: the calendar isn't wired yet and the team should know
  // this list is the only place these live for now.
  calendarSoon: {
    marginLeft: 'auto',
    fontSize: 11.5,
    color: '#7f8d9c',
    border: '1px dashed #d9e1e9',
    borderRadius: 6,
    padding: '3px 9px',
    whiteSpace: 'nowrap'
  },
  // The decision chips under the reason line.
  chips: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 },
  chip: {
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: 5,
    background: '#f2f5f9',
    color: '#48586a',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums'
  },
  chipAction: { cursor: 'pointer', background: '#edf1ff', color: '#2540c9' },
  empty: { padding: 28, textAlign: 'center', color: '#9aa7b3', border: '1px solid #eef2f6', borderRadius: 8 },
  construction: {
    padding: 34,
    textAlign: 'center',
    border: '1px dashed #c3d0dd',
    borderRadius: 8,
    background: '#f6f8fa'
  },
  // Uneven coverage: the rows are real and still shown, so this is a note
  // above them, not a wall in front of them.
  caveat: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    padding: '9px 12px',
    border: '1px solid #f0e0c2',
    borderRadius: 8,
    background: '#fdf8ef',
    fontSize: 12.5,
    color: '#7a6234',
    marginBottom: 12
  }
}))

// A lane shows its top slice and nothing more until asked. Indiranagar came
// back with 1,899 actionable parents across the lanes — an uncapped list is
// the 12,000-row page we set out to replace, only smaller. Rows are already
// ranked by rupees, so the top of a lane is the part worth working.
const LANE_CAP = 15

const MyDay = () => {
  const classes = useStyles()
  const [month, setMonth] = useState(currentMonth())
  const [centres, setCentres] = useState([])
  const [centreUuid, setCentreUuid] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [category, setCategory] = useState('afterschool')
  const [lane, setLane] = useState('at_risk')
  // At Risk sub-filter by trigger flag (user 2026-07-29): all / needs recharge
  // / discovery ending / barely attending / unhappy.
  const [atRiskFilter, setAtRiskFilter] = useState('all')
  // Autopay filter — works across any lane: 'all' | 'yes' | 'no'.
  const [autopayFilter, setAutopayFilter] = useState('all')
  // Active-lane sort (user 2026-08-07): default name order, or by coins expiry.
  const [activeSort, setActiveSort] = useState('name')
  // Winback chips (user 2026-07-30): default list is ALL inactive parents
  // (coins-expiry-soonest → coins desc). Chips narrow by lapsed age / coins band
  // / coins-expiry band / subject. Default = no filter ('all' everywhere).
  const [wb, setWb] = useState({
    lapsed: 'all', coins: 'all', expiry: 'all', subject: 'all'
  })
  // Only ONE filter active at a time (user 2026-07-30): selecting any chip
  // resets every other dimension to 'all', so the previous filter deselects.
  const setWbKey = (k, v) =>
    setWb({ lapsed: 'all', coins: 'all', expiry: 'all', subject: 'all', [k]: v })
  const [showAllLanes, setShowAllLanes] = useState({})
  // The pipeline rides in with the page payload; these mirror it so a click
  // updates immediately instead of waiting for a refetch.
  const [pipeline, setPipeline] = useState(null)
  const [pipelineOpen, setPipelineOpen] = useState(false)
  const [followUpsOpen, setFollowUpsOpen] = useState(false)
  const [followUpFilter, setFollowUpFilter] = useState('all')
  const [pipelineAdder, setPipelineAdder] = useState('')
  const [pipelineBusy, setPipelineBusy] = useState(null)
  const [addForm, setAddForm] = useState(null)
  // Category level inside each panel — independent of the page's category bar,
  // because a team member opening Follow-ups is asking a different question
  // from whichever lane filter they happened to leave on.
  const [followUpCat, setFollowUpCat] = useState('all')
  const [pipelineCat, setPipelineCat] = useState('all')
  // All active enrolments — a reference list, not a call list, so it lives at
  // the end of the category row and opens in place of the lanes like the other
  // two panels. Loaded on demand: it is the largest query on the page.
  const [enrolledOpen, setEnrolledOpen] = useState(false)
  const [enrolled, setEnrolled] = useState(null)
  const [enrolledLoading, setEnrolledLoading] = useState(false)
  const [enrolledSearch, setEnrolledSearch] = useState('')
  // Pending centre-parent chats — its own tab, its own fetch (user 2026-07-29).
  const [chatsOpen, setChatsOpen] = useState(false)
  const [chats, setChats] = useState(null)
  const [chatsLoading, setChatsLoading] = useState(false)
  const [chatsSearch, setChatsSearch] = useState('')
  // Interaction Audit tab — prospects visited but no interaction logged yet.
  const [iaOpen, setIaOpen] = useState(false)
  const [ia, setIa] = useState(null)
  const [iaLoading, setIaLoading] = useState(false)
  const [iaSearch, setIaSearch] = useState('')
  // Conversion "why-not" reports (recharge / discovery / trials) — self-fetching.
  const [reportsOpen, setReportsOpen] = useState(false)
  // Focus for the day — the four daily must-do targets, the landing tab.
  const [focusOpen, setFocusOpen] = useState(false)
  const [focus, setFocus] = useState(null)
  const [focusLoading, setFocusLoading] = useState(false)
  const [focusOpenTarget, setFocusOpenTarget] = useState(null)
  // Trends — the centre's daily connect input over a date range (Focus over
  // time). Defaults to the last 14 days ending today.
  const [trendsOpen, setTrendsOpen] = useState(false)
  const [trends, setTrends] = useState(null)
  const [trendsLoading, setTrendsLoading] = useState(false)
  const [trendStart, setTrendStart] = useState(() => localDateStr(new Date(Date.now() - 13 * 864e5)))
  const [trendEnd, setTrendEnd] = useState(() => localDateStr())
  // Centre Scoreboard — every in-scope centre's score /4 for a chosen day.
  const [cockpitOpen, setCockpitOpen] = useState(false)
  const [cockpit, setCockpit] = useState(null)
  const [cockpitLoading, setCockpitLoading] = useState(false)
  const [cockpitDate, setCockpitDate] = useState(() => localDateStr())
  // Clicking a Scoreboard row switches to that centre THEN opens its Focus —
  // but the centre change reloads + closes panels, so we open Focus only once
  // the new centre's data has landed (see the effect below).
  const [pendingFocus, setPendingFocus] = useState(false)
  // Daily Connected — team-input report, centre + day scoped.
  const [dcOpen, setDcOpen] = useState(false)
  const [dc, setDc] = useState(null)
  const [dcLoading, setDcLoading] = useState(false)
  const [dcDate, setDcDate] = useState(() => localDateStr())
  const [dcOpenPerson, setDcOpenPerson] = useState(null)
  const [dcCohort, setDcCohort] = useState('all')   // 'all' | 'programme:cohort'
  // Programme filter INSIDE the enrolled panel (Shruti, 2026-07-24). Separate
  // from the page's `category` on purpose: this list is not the lanes, and
  // filtering one must not move the other.
  const [enrolledType, setEnrolledType] = useState('all')
  const [drawerParent, setDrawerParent] = useState(null)

  useEffect(() => {
    const loadCentres = async () => {
      try {
        const summary = await fetchRevenueGoalSummary(month)
        const rows = summary?.centres || []
        setCentres(rows)
        const wanted = new URLSearchParams(window.location.search).get('centre')
        setCentreUuid((current) => {
          if (wanted && rows.some((r) => r.centre_uuid === wanted)) return wanted
          if (current && rows.some((r) => r.centre_uuid === current)) return current
          return rows.length ? rows[0].centre_uuid : ''
        })
      } catch (error) {
        showToast('Could not load centres', 'error')
      }
    }
    loadCentres()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const load = useCallback(async (m, c) => {
    if (!c) return
    setLoading(true)
    try {
      const payload = await fetchMyDay(m, c)
      setData(payload)
      setPipeline(payload?.pipeline || null)
    } catch (error) {
      showToast('Could not load My Day', 'error')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(month, centreUuid)
    // The active-enrolled roster follows the selected MONTH + centre (user
    // 2026-08-09): if the tab is open, reload it for the new selection and keep
    // it open (a past month shows who was active THEN) instead of forcing a
    // reopen. Closed → just clear the stale list.
    if (enrolledOpen && centreUuid) {
      setEnrolledLoading(true)
      fetchEnrollmentDirectory(centreUuid, 'active', month)
        .then(setEnrolled)
        .catch(() => {})
        .finally(() => setEnrolledLoading(false))
    } else {
      setEnrolled(null)
    }
    setChats(null)
    setChatsOpen(false)
    setIa(null)
    setIaOpen(false)
    // Daily Connected is per-centre + per-day — a stale person list from the
    // previous centre (e.g. HRBR's Varsha lingering under Haralur) is the exact
    // bug this prevents. Close it; re-opening reloads for the new centre.
    setDc(null)
    setDcOpen(false)
    setFocus(null)
    setFocusOpen(false)
    setTrends(null)
    setTrendsOpen(false)
    setCockpitOpen(false)
    setFocusOpenTarget(null)
    setDcOpenPerson(null)
    // The next centre may not run the programme that was selected, which would
    // open the panel on an empty filter.
    setEnrolledType('all')
    setAtRiskFilter('all')
    setAutopayFilter('all')
    setWb({ lapsed: 'all', coins: 'all', expiry: 'all', subject: 'all' })
  }, [month, centreUuid, load])

  // If the selected programme isn't offered at this centre, fall back to one
  // that is (afterschool is offered everywhere in practice), so switching to an
  // afterschool-only centre never lands on an empty hidden tab.
  useEffect(() => {
    const offered = data?.programmes
    if (offered && offered.length && !offered.includes(category)) {
      setCategory(offered.includes('afterschool') ? 'afterschool' : offered[0])
    }
  }, [data, category])

  const underConstruction = (data?.under_construction || []).includes(category)

  // Families with no programme tab of their own. Counted and stated rather
  // than silently absent -- spec §1.
  const uncoveredCount =
    (data?.category_counts?.daycare || 0) + (data?.category_counts?.other || 0)

  // One category filter over every lane. A parent belonging to two categories
  // shows under each.
  const lanes = useMemo(() => {
    const out = {}
    ;(data?.lane_order || []).forEach((lane) => {
      const rows = (data?.lanes?.[lane] || []).filter(
        (r) => category === 'all' || (r.categories || []).includes(category)
      )
      out[lane] = rows
    })
    return out
  }, [data, category])

  const catColor = (c) =>
    ({ preschool: '#6242c9', afterschool: '#2f4fd8', daycare: '#0f7a56' }[c] || '#7a8798')

  // Adding asks for the two things the decision was made on; removing is
  // immediate. Both return the whole refreshed pipeline, so there is no local
  // state to reconcile — just take what the server says.
  const openEnrolled = async () => {
    const next = !enrolledOpen
    setEnrolledOpen(next)
    setChatsOpen(false)
    setIaOpen(false)
    setFollowUpsOpen(false)
    setPipelineOpen(false)
    setReportsOpen(false)
    setDcOpen(false)
    setFocusOpen(false)
    setTrendsOpen(false)
    setCockpitOpen(false)
    if (!next || !centreUuid) return
    setEnrolledLoading(true)
    try {
      // 'active' only — the user asked for active students, and it is also
      // what keeps this list a usable size. Scoped to the SELECTED month (user
      // 2026-08-09): a past month shows who was active THEN, not today.
      setEnrolled(await fetchEnrollmentDirectory(centreUuid, 'active', month))
    } catch (error) {
      showToast('Could not load the enrolled list', 'error')
      setEnrolledOpen(false)
    } finally {
      setEnrolledLoading(false)
    }
  }

  const openChats = async () => {
    const next = !chatsOpen
    setChatsOpen(next)
    setIaOpen(false)
    setEnrolledOpen(false)
    setFollowUpsOpen(false)
    setPipelineOpen(false)
    setReportsOpen(false)
    setDcOpen(false)
    setFocusOpen(false)
    setTrendsOpen(false)
    setCockpitOpen(false)
    if (!next || !centreUuid) return
    setChatsLoading(true)
    try {
      setChats(await fetchPendingChats(centreUuid))
    } catch (error) {
      showToast('Could not load pending chats', 'error')
      setChatsOpen(false)
    } finally {
      setChatsLoading(false)
    }
  }

  const openIa = async () => {
    const next = !iaOpen
    setIaOpen(next)
    setChatsOpen(false)
    setEnrolledOpen(false)
    setFollowUpsOpen(false)
    setPipelineOpen(false)
    setReportsOpen(false)
    setDcOpen(false)
    setFocusOpen(false)
    setTrendsOpen(false)
    setCockpitOpen(false)
    if (!next || !centreUuid) return
    setIaLoading(true)
    try {
      setIa(await fetchInteractionsNotLogged(centreUuid))
    } catch (error) {
      showToast('Could not load interaction audit', 'error')
      setIaOpen(false)
    } finally {
      setIaLoading(false)
    }
  }

  const loadDc = async (day) => {
    if (!centreUuid) return
    setDcCohort('all')
    setDcOpenPerson(null)
    setDcLoading(true)
    try {
      const res = await fetchDailyConnected(centreUuid, day)
      setDc(res)
      // Trust the server's notion of the day it actually queried, so the picker
      // and the data can never drift (e.g. a client/UTC date mismatch).
      if (res?.day) setDcDate(res.day)
    } catch (error) {
      showToast('Could not load daily connected', 'error')
    } finally {
      setDcLoading(false)
    }
  }

  const openDc = () => {
    const next = !dcOpen
    setDcOpen(next)
    setEnrolledOpen(false)
    setChatsOpen(false)
    setIaOpen(false)
    setFollowUpsOpen(false)
    setPipelineOpen(false)
    setReportsOpen(false)
    setFocusOpen(false)
    setTrendsOpen(false)
    setCockpitOpen(false)
    setDcOpenPerson(null)
    if (next) loadDc(dcDate)
  }

  const loadFocus = async () => {
    if (!centreUuid) return
    setFocusLoading(true)
    try {
      setFocus(await fetchFocus(centreUuid))
    } catch (error) {
      showToast('Could not load focus', 'error')
    } finally {
      setFocusLoading(false)
    }
  }

  const openFocus = () => {
    const next = !focusOpen
    setFocusOpen(next)
    setEnrolledOpen(false)
    setChatsOpen(false)
    setIaOpen(false)
    setFollowUpsOpen(false)
    setPipelineOpen(false)
    setReportsOpen(false)
    setDcOpen(false)
    setTrendsOpen(false)
    setCockpitOpen(false)
    setFocusOpenTarget(null)
    if (next) loadFocus()
  }

  // Follow-ups has no open-handler of its own (its title-row button toggles
  // inline). Focus targets jump INTO the existing tab rather than re-listing
  // its rows, so it needs one. Opens (never toggles) and closes the rest.
  const openFollowUps = () => {
    setFollowUpsOpen(true)
    setFocusOpen(false)
    setTrendsOpen(false)
    setCockpitOpen(false)
    setPipelineOpen(false)
    setEnrolledOpen(false)
    setChatsOpen(false)
    setIaOpen(false)
    setReportsOpen(false)
    setDcOpen(false)
    setFocusOpenTarget(null)
  }

  // Trends = every centre's score /4 over the range (cross-centre, not the
  // selected centre). Reads the score ledger + today's live score.
  const loadTrends = async (start, end) => {
    setTrendsLoading(true)
    try {
      setTrends(await fetchCentreScoreTrends(start, end))
    } catch (error) {
      showToast('Could not load trends', 'error')
    } finally {
      setTrendsLoading(false)
    }
  }

  const loadCockpit = async (day) => {
    setCockpitLoading(true)
    try {
      setCockpit(await fetchCockpit(day))
    } catch (error) {
      showToast('Could not load cockpit', 'error')
    } finally {
      setCockpitLoading(false)
    }
  }

  const openCockpit = () => {
    const next = !cockpitOpen
    setCockpitOpen(next)
    setTrendsOpen(false)
    setFocusOpen(false)
    setEnrolledOpen(false)
    setChatsOpen(false)
    setIaOpen(false)
    setFollowUpsOpen(false)
    setPipelineOpen(false)
    setReportsOpen(false)
    setDcOpen(false)
    setFocusOpenTarget(null)
    if (next) loadCockpit(cockpitDate)
  }

  const openTrends = () => {
    const next = !trendsOpen
    setTrendsOpen(next)
    setCockpitOpen(false)
    setFocusOpen(false)
    setEnrolledOpen(false)
    setChatsOpen(false)
    setIaOpen(false)
    setFollowUpsOpen(false)
    setPipelineOpen(false)
    setReportsOpen(false)
    setDcOpen(false)
    setFocusOpenTarget(null)
    if (next) loadTrends(trendStart, trendEnd)
  }

  // Drill from the Scoreboard: after a row switches the centre, the centre
  // effect reloads and closes every panel; once that centre's data is in,
  // open its Focus. Fires once, then clears the flag.
  useEffect(() => {
    if (pendingFocus && !loading && data && centreUuid) {
      setPendingFocus(false)
      openFocus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFocus, loading, data, centreUuid])

  const openAddDialog = (row) => {
    setAddForm({
      row,
      // Seeded from the row as a sensible default, but editable — the parent
      // may well be asking about a programme they don't already take.
      // A family can want more than one programme, so this is a list. Seeded
      // from the row as a sensible default, but fully editable.
      category: PIPELINE_CATEGORIES.includes(row?.primary_category)
        ? [row.primary_category]
        : ['afterschool'],
      tentative_month: '',
      grade: '',
      subjects: [],
      subjectsOther: ''
    })
  }

  // Every pipeline field is mandatory except the free-text "Anything else"
  // (user 2026-08-07). Grade/Subjects are only required when their section is
  // shown for the chosen category. Subjects is satisfied by a picked subject OR
  // a free-typed one, so a centre with an empty catalogue is never a dead end.
  const pipelineMissing = (() => {
    const f = addForm
    if (!f) return []
    const cats = f.category || []
    const gradeShown = cats.some((c) => GRADE_CATEGORIES.includes(c))
    const subjShown = cats.some((c) => !GRADE_CATEGORIES.includes(c))
    const typed = (f.subjectsOther || '').trim()
    const miss = []
    if (!cats.length) miss.push('Category')
    if (!f.tentative_month) miss.push('Tentative month')
    if (gradeShown && !f.grade) miss.push('Grade')
    if (subjShown && !((f.subjects || []).length || typed)) miss.push('Subjects')
    return miss
  })()

  const submitAdd = async () => {
    if (!addForm || !centreUuid) return
    if (pipelineMissing.length) {
      showToast(`Fill in: ${pipelineMissing.join(', ')}`, 'error')
      return
    }
    const row = addForm.row
    setPipelineBusy(row.parent_user_uuid)
    try {
      // Free-typed subjects ride along with the picked ones — a parent asking
      // for something not on the catalogue is the case worth capturing.
      const typed = (addForm.subjectsOther || '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
      const next = await addToPipeline(centreUuid, row.parent_user_uuid, {
        category: addForm.category,
        tentative_month: addForm.tentative_month,
        grade: addForm.grade,
        subjects: [...(addForm.subjects || []), ...typed]
      })
      setPipeline(next)
      setAddForm(null)
      showToast('Added to pipeline')
    } catch (error) {
      showToast('Could not add to the pipeline', 'error')
    } finally {
      setPipelineBusy(null)
    }
  }

  const removePipeline = async (parentUuid) => {
    if (!centreUuid || pipelineBusy) return
    setPipelineBusy(parentUuid)
    try {
      setPipeline(await removeFromPipeline(centreUuid, parentUuid))
      showToast('Removed from pipeline')
    } catch (error) {
      showToast('Could not update the pipeline', 'error')
    } finally {
      setPipelineBusy(null)
    }
  }

  const toneColor = { bad: '#cf3a3a', warn: '#b06800', ok: '#128560' }
  const toneBg = { bad: '#fceaea', warn: '#fbefdb', ok: '#e6f4ee' }

  // The decision chips, capped so a row stays one glance.
  // P4 (user 2026-07-23): both panels work at TWO levels — the whole centre,
  // and one programme category within it. Same control in both, so the strip is
  // built once. It filters; it never hides a count.
  const catStrip = (rows, value, onPick) => {
    const counts = { all: rows.length }
    rows.forEach((r) => {
      ;(r.categories || ['other']).forEach((c) => {
        counts[c] = (counts[c] || 0) + 1
      })
    })
    return (
      <Box className={classes.catBar} style={{ marginTop: 8 }}>
        {CATEGORY_ORDER.filter((c) => c === 'all' || counts[c] || c === value).map((c) => (
          <span
            key={c}
            className={`${classes.catBtn} ${value === c ? classes.catBtnOn : ''}`}
            onClick={() => onPick(c)}
          >
            {CATEGORY_LABEL[c]}
            <span className={classes.catCount}>{counts[c] || 0}</span>
          </span>
        ))}
      </Box>
    )
  }

  const inCategory = (row, cat) =>
    cat === 'all' || (row.categories || ['other']).includes(cat)

  const renderChips = (row) => {
    // `recharge_due` is a FLAG, not a measurement, so it does not arrive in
    // detail_fields. Fold it in here so the chip table stays one list and the
    // MAX_CHIPS cap keeps counting correctly.
    const df = (row.flags || []).includes('recharge_due')
      ? { recharge_due: true, ...(row.detail_fields || {}) }
      : row.detail_fields || {}
    const skip = LANE_CHIP_SKIP[row.lane] || []
    const out = []
    for (const [key, , fmt, tone] of CHIPS) {
      if (out.length >= MAX_CHIPS) break
      if (skip.includes(key)) continue
      const raw = df[key]
      if (raw === undefined || raw === null || raw === '') continue
      const text = fmt(raw, df)
      if (!text) continue
      const t = tone ? tone(raw, df) : null
      out.push(
        <span
          key={key}
          className={classes.chip}
          style={t ? { background: toneBg[t], color: toneColor[t] } : undefined}
        >
          {text}
        </span>
      )
    }
    if (df.payment_link) {
      out.push(
        <span
          key="copy"
          className={`${classes.chip} ${classes.chipAction}`}
          onClick={(e) => {
            e.stopPropagation()
            // Clipboard write must be synchronous — an await before this makes
            // the browser refuse it.
            navigator.clipboard.writeText(df.payment_link)
            showToast('Payment link copied')
          }}
        >
          copy payment link
        </span>
      )
    }
    return out.length ? <Box className={classes.chips}>{out}</Box> : null
  }

  const renderRow = (row) => {
    // A parent can be in a lane for more than one programme. With a category
    // selected, show only THAT programme's reason and warning signs — the
    // afterschool wallet has nothing to do with the preschool call you are
    // about to make (Shruti, 2026-07-24). On All, the union is right.
    const view =
      (category !== 'all' && row.by_category?.[category]) || row
    // Which category chips to print. At risk: none once a category is picked,
    // every row is that category. Grow: ALL of them, always — knowing the
    // parent asked about afterschool as well as preschool is the point of the
    // conversation (Shruti, 2026-07-24).
    // Grow still prints every category the parent is interested in -- that
    // they asked about afterschool as well as preschool IS the conversation.
    // Everywhere else the tab already says the programme, so no chip.
    const chipCats = activeLane === 'grow' ? (row.categories || []) : []
    return (
    <Box
      className={classes.row}
      // Afterschool At Risk is one row per enrolment, so a parent can appear
      // twice in the same lane — the key has to carry the enrolment too.
      key={`${row.parent_user_uuid}-${row.lane}-${row.enrolment_uuid || ''}`}
      // Actioned rows (contacted today) are dimmed — worked, but still visible.
      style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: row.actioned ? 0.5 : 1 }}
    >
      {row.actioned && (
        <span
          className={classes.chip}
          style={{ background: '#e6f4ee', color: '#0f7a56', whiteSpace: 'nowrap' }}
          title="Someone logged a call with this parent today"
        >
          ✓ Contacted today
        </span>
      )}
      {chipCats.length > 0 && (
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {chipCats.map((c) => (
            <span
              key={c}
              className={classes.cat}
              style={{ background: `${catColor(c)}18`, color: catColor(c) }}
            >
              {CATEGORY_LABEL[c] || c}
            </span>
          ))}
        </span>
      )}
      <span className={classes.who}>
        <a
          href={`/home/parent?parent=${encodeURIComponent(row.parent_user_uuid)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1565C0', textDecoration: 'none' }}
        >
          <span className={classes.name}>{row.parent_name}</span>
        </a>
        {(() => {
          // Winback leads with the interaction status (intent_label). At Risk
          // replaces the generic "interaction suggests they are unhappy" line
          // with the ACTUAL last note (user 2026-07-28) — a real quote beats a
          // label. Factual reasons (needs recharge, low attendance, discovery)
          // are kept; the note then rides underneath as context.
          const atRiskUnhappy =
            row.lane === 'at_risk' && /unhappy/i.test(view.reason || '')
          // At Risk: lead with the ACTION NUDGE — what to do on this call (user
          // 2026-07-29) — coloured amber, with the parent's actual last note
          // underneath as the specifics. Grow does the same with its tone-nudge.
          const atRiskNudge = row.lane === 'at_risk' ? row.action_nudge : null
          const growTone = row.lane === 'grow' ? row.nudge_tone : null
          const GROW_COLOR = {
            untouched: '#c2185b', ready: '#2e7d32', concern: '#cf3a3a',
            cold: '#b06800', fresh: '#1565C0', switched: '#6a6a6a', followup: '#6a6a6a'
          }
          // Pipeline: a High/Medium/Low intent chip leads, sources + note follow.
          const PIPELINE_COLOR = { high: '#2e7d32', medium: '#b06800', low: '#6a6a6a' }
          const pipelineTier = row.lane === 'pipeline' ? row.pipeline_tier : null
          // Preschool Grow is categorised H/M/L basis interaction (user
          // 2026-07-30), with "not logged" as its own top state. Only preschool
          // grow rows carry grow_tier, so the chip simply shows when present.
          const TIER_COLOR = { ...PIPELINE_COLOR, not_logged: '#c2185b' }
          const growTier = row.lane === 'grow' ? row.grow_tier : null
          const growTierLabel = growTier === 'not_logged' ? 'Not logged' : `${growTier} intent`
          const reasonText =
            row.lane === 'active'
              // child name | subject (user 2026-08-02). The subject is NOT
              // prepended separately for active rows (see below), so it isn't
              // shown twice.
              ? [row.child_name, row.subject].filter(Boolean).join(' | ') || row.grade || 'Enrolled'
              : row.lane === 'winback' && row.intent_label
                ? row.intent_label
                : atRiskNudge
                  ? atRiskNudge
                  : (atRiskUnhappy || row.lane === 'churned') && row.summary
                    ? row.summary
                    : view.reason || '—'
          const reasonColor = growTone
            ? GROW_COLOR[growTone]
            : atRiskNudge
              ? '#b06800'
              : undefined
          const noteLine =
            row.lane === 'winback'
              ? row.summary
              : row.lane === 'at_risk'
                ? row.summary
                : row.lane === 'grow'
                  ? row.summary
                  : row.lane === 'pipeline'
                    ? row.summary
                    : null
          return (
            <>
              <div
                className={classes.reason}
                style={reasonColor ? { color: reasonColor, fontWeight: 500 } : undefined}
              >
                {pipelineTier ? (
                  <span
                    className={classes.chip}
                    style={{
                      marginRight: 6, textTransform: 'capitalize', fontWeight: 600,
                      background: `${PIPELINE_COLOR[pipelineTier]}18`,
                      color: PIPELINE_COLOR[pipelineTier]
                    }}
                  >
                    {pipelineTier} intent
                  </span>
                ) : null}
                {growTier ? (
                  <span
                    className={classes.chip}
                    style={{
                      marginRight: 6, textTransform: 'capitalize', fontWeight: 600,
                      background: `${TIER_COLOR[growTier]}18`,
                      color: TIER_COLOR[growTier]
                    }}
                  >
                    {growTierLabel}
                  </span>
                ) : null}
                {view.subject && row.lane !== 'active' ? `${view.subject} — ` : ''}
                {reasonText}
                {/* Autopay = confirmed e-mandate (user 2026-08-02): recharge is
                    on autopilot, so a needs-recharge row here is low-priority. */}
                {row.autopay ? (
                  <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 600, color: '#2e7d32', background: '#e8f5e9', borderRadius: 6, padding: '1px 6px', verticalAlign: 'middle' }}>
                    Autopay
                  </span>
                ) : null}
              </div>
              {noteLine ? (
                <div className={classes.last} style={{ fontStyle: 'italic', opacity: 0.85 }}>
                  “{noteLine}”
                </div>
              ) : null}
            </>
          )
        })()}
        {/* Pipeline pickup chips (user 2026-08-07): the tentative joining month
            + categories the pickup was judged on. Read from the pipeline
            bookmark (`by_parent`), so they render on ANY lane where the parent
            is in the pipeline — including the Pipeline lane itself, which is
            where the team actually looks. */}
        {(() => {
          const p = pipeline?.by_parent?.[row.parent_user_uuid]
          if (!p) return null
          const cats = (p.categories || []).filter((c) => c && c !== 'other')
          const subs = (p.subjects || '').split(',').map((s) => s.trim()).filter(Boolean)
          if (!p.tentative_month && !cats.length && !p.grade && !subs.length) return null
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 3 }}>
              {p.tentative_month && (
                <span className={classes.chip} style={{ background: '#fff4e5', color: '#B26A00' }}>
                  Joining ~ {monthLabel(p.tentative_month)}
                </span>
              )}
              {cats.map((c) => (
                <span key={c} className={classes.chip} style={CAT_CHIP[c] || CAT_CHIP.other}>
                  {CATEGORY_LABEL[c] || c}
                </span>
              ))}
              {p.grade && <span className={classes.chip}>{p.grade}</span>}
              {subs.map((s) => (
                <span key={s} className={classes.chip}>{s}</span>
              ))}
            </div>
          )
        })()}
        <div className={classes.last}>
          {/* Phone is back on the row (spec §3.3, ruling 2026-07-24). We had
              removed it on 2026-07-22 on the argument that the row is for
              deciding who to call and the profile is one click away; the
              ruling went the other way. It was always in the payload. */}
          {row.phone ? <span className={classes.rowPhone}>{row.phone}</span> : null}
          {row.last_contact
            ? `Last contact ${dayMonthYear(row.last_contact)}`
            : row.lane === 'active'
              ? ''
              : 'Never contacted'}
          {row.recently_contacted ? ' · called in the last 7 days' : ''}
        </div>
        {renderChips({ ...row, ...view })}
      </span>
      {/* The per-row amount and the phone number are gone (user 2026-07-22).
          at_stake still ORDERS At risk and Grow, it is just no longer printed
          on the line; the lane header keeps the total. The number lives at the
          top of the call-log drawer, one click away, so repeating it on every
          row only widened the line. */}
      {/* The pipeline is for NEW ACQUISITIONS only (Shruti, 2026-07-24): first
          visits and logged enquiries. A parent who already pays us and turns
          up in At Risk is not someone to "pick up", so the button is not
          offered there. A row already IN the pipeline still shows its chip and
          its remove button wherever it appears — otherwise anything added
          before this change would be stuck there with no way out. */}
      {(() => {
        const p = pipeline?.by_parent?.[row.parent_user_uuid]
        // The pipeline is PRESCHOOL-ONLY and MANUAL (user 2026-07-30): the team
        // picks families up from where preschool prospects actually surface —
        // preschool Grow, Toddler Club Grow, active Toddler Club, and Toddler
        // Club Winback (a lapsed TC family is still a preschool feeder — user
        // 2026-08-07). Afterschool has no pipeline, so its rows never offer the
        // button. On the All tab the row's own categories decide, so a
        // preschool/TC grow or TC-winback row still qualifies.
        const rowIsPreschoolFeed = (row.categories || []).some(
          (c) => c === 'preschool' || c === 'toddler_club'
        )
        const pipelineEligible =
          ((activeLane === 'grow' || activeLane === 'winback') &&
            (category === 'preschool' ||
              category === 'toddler_club' ||
              (category === 'all' && rowIsPreschoolFeed))) ||
          (activeLane === 'active' && category === 'toddler_club')
        if (!p) {
          if (!pipelineEligible) return null
          return (
            <Button
              size="small"
              variant="outlined"
              disableElevation
              disabled={pipelineBusy === row.parent_user_uuid}
              onClick={() => openAddDialog(row)}
              style={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Add to pipeline
            </Button>
          )
        }
        // In the pipeline: state it, and say plainly what the button does —
        // the label used to read "In pipeline · …" and silently remove on click.
        return (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <span
              className={classes.chip}
              style={{ background: '#e6f4ee', color: '#0f7a56' }}
            >
              In pipeline · {p.added_by_short} · {dayMonthYear(p.added_on)}
            </span>
            <Button
              size="small"
              variant="outlined"
              disabled={pipelineBusy === row.parent_user_uuid}
              onClick={() => removePipeline(row.parent_user_uuid)}
              style={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Remove from pipeline
            </Button>
          </Box>
        )
      })()}
      <Button
        size="small"
        variant="contained"
        color="primary"
        style={{ textTransform: 'none' }}
        onClick={() =>
          setDrawerParent({ uuid: row.parent_user_uuid, name: row.parent_name, phone: row.phone })
        }
      >
        Add interaction
      </Button>
    </Box>
    )
  }

  // One lane at a time. Counts sit on every tab, so a lane can never be
  // silently scrolled past the way it could when they were stacked.
  // Lanes shown for the programme tab in view. Preschool has no Winback --
  // only a wallet holds money and preschool has none -- so the tab is not
  // offered rather than shown reading zero.
  const lanesForProgramme = (data?.lane_order || []).filter(
    (key) => (LANES_BY_PROGRAMME[category] || data?.lane_order || []).includes(key)
  )

  const activeLane = lanesForProgramme.includes(lane)
    ? lane
    : lanesForProgramme[0] || lane

  const renderLaneTabs = () => (
    <Box className={classes.laneTabs}>
      {lanesForProgramme.map((key) => {
        const meta = data?.lane_meta?.[key] || {}
        const n = (lanes[key] || []).length
        return (
          <Tooltip key={key} title={meta.blurb || ''}>
            <span
              className={`${classes.laneTab} ${activeLane === key ? classes.laneTabOn : ''}`}
              onClick={() => { setLane(key); setAtRiskFilter('all'); setAutopayFilter('all') }}
            >
              <span
                className={classes.swatch}
                style={{ background: LANE_COLOR[key], display: 'inline-block', marginRight: 7 }}
              />
              {meta.label || key}
              <span className={classes.laneTabCount}>{n}</span>
            </span>
          </Tooltip>
        )
      })}
    </Box>
  )

  const renderActiveLane = () => {
    const allRows = lanes[activeLane] || []
    const meta = data?.lane_meta?.[lane] || {}
    // At Risk filter chips (user 2026-07-29): narrow by trigger flag. A chip
    // only appears when rows carry that flag, so it stays afterschool-only.
    const isAtRisk = activeLane === 'at_risk'
    const flagChips = isAtRisk
      ? AT_RISK_FILTERS
          .map(([flag, label]) => [flag, label,
            allRows.filter((r) => (r.flags || []).includes(flag)).length])
          .filter(([, , n]) => n > 0)
      : []
    // Winback filtering (user 2026-07-30): narrow the whole inactive pool by the
    // chips — afterschool: lapsed / coins / expiry / subject; toddler club: just
    // "ended in" bands (no wallet, one programme).
    const isWinbackAf = activeLane === 'winback' && category === 'afterschool'
    const isWinbackTc = activeLane === 'winback' && category === 'toddler_club'
    const isWinback = isWinbackAf || isWinbackTc
    const wbMatch = (r) =>
      wbBand('lapsed', wb.lapsed, r) &&
      wbBand('coins', wb.coins, r) &&
      wbBand('expiry', wb.expiry, r) &&
      (wb.subject === 'all' || (r.subjects || []).some((s) => s.startsWith(wb.subject)))
    const filteredBase = (isAtRisk && atRiskFilter !== 'all')
      ? allRows.filter((r) => (r.flags || []).includes(atRiskFilter))
      : isWinback
        ? allRows.filter(wbMatch)
        : allRows
    // Autopay filter (user 2026-08-02): works in ANY lane — narrow to parents
    // who have / don't have a confirmed e-mandate.
    const autopayCount = allRows.filter((r) => r.autopay).length
    const filtered = autopayFilter === 'yes'
      ? filteredBase.filter((r) => r.autopay)
      : autopayFilter === 'no'
        ? filteredBase.filter((r) => !r.autopay)
        : filteredBase
    // Actioned rows (someone logged a call today — team-wide, from the backend)
    // sink to the BOTTOM of the lane, keeping their order. Worked for now, but
    // still visible in case it needs another look (user 2026-07-30).
    // Active lane can be sorted by coins expiry (soonest first) instead of the
    // default name order (user 2026-08-07). Missing expiry sorts last.
    const coinExpiry = (r) => (r.detail_fields || {}).coins_expiry || '9999-12-31'
    const base = (activeLane === 'active' && activeSort === 'coins_expiry')
      ? [...filtered].sort((a, b) => coinExpiry(a).localeCompare(coinExpiry(b)))
      : filtered
    const rows = [
      ...base.filter((r) => !r.actioned),
      ...base.filter((r) => r.actioned)
    ]
    const wbChip = (active, label, count, onClick) => (
      <span
        key={label}
        className={`${classes.catBtn} ${active ? classes.catBtnOn : ''}`}
        onClick={onClick}
      >
        {label}
        {count != null && <span className={classes.catCount}>{count}</span>}
      </span>
    )
    // one filter dimension = a labelled row of single-select chips with counts
    const wbGroup = (dim, label, options) => (
      <Box className={classes.catBar} style={{ marginTop: 0, marginBottom: 4, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#9aa7b3', width: 56, flexShrink: 0 }}>{label}</span>
        {options.map(([k, lbl]) => wbChip(
          wb[dim] === k, lbl,
          allRows.filter((r) => wbBand(dim, k, r)).length,
          () => setWbKey(dim, k)
        ))}
      </Box>
    )
    return (
      <Box className={classes.lane}>
        {isWinbackTc && allRows.length > 0 && (
          <Box style={{ marginBottom: 10 }}>
            {wbGroup('lapsed', 'Ended', WB_LAPSED)}
          </Box>
        )}
        {isWinbackAf && allRows.length > 0 && (
          <Box style={{ marginBottom: 10 }}>
            {wbGroup('lapsed', 'Lapsed', WB_LAPSED)}
            {wbGroup('coins', 'Coins', WB_COINS)}
            {wbGroup('expiry', 'Expiry', WB_EXPIRY)}
            <Box className={classes.catBar} style={{ marginTop: 0, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#9aa7b3', width: 56, flexShrink: 0 }}>Subject</span>
              {wbChip(wb.subject === 'all', 'All',
                allRows.length, () => setWbKey('subject', 'all'))}
              {WB_SUBJECTS
                .filter((s) => allRows.some((r) => (r.subjects || []).some((x) => x.startsWith(s))))
                .map((s) => wbChip(
                  wb.subject === s, s.replace(' & Design', ''),
                  allRows.filter((r) => (r.subjects || []).some((x) => x.startsWith(s))).length,
                  () => setWbKey('subject', s)
                ))}
            </Box>
          </Box>
        )}
        {/* The lane head carried the blurb ("Paying now, something is wrong…")
            and the lane total ("₹72,000 at stake"). Both removed on Shruti's
            ask (2026-07-24). The blurb is still the tab's tooltip, and
            at_stake still ORDERS the lane — it is only no longer printed. */}
        {flagChips.length > 0 && (
          <Box className={classes.catBar} style={{ marginTop: 0, marginBottom: 10 }}>
            {[['all', 'All', allRows.length], ...flagChips].map(([key, label, n]) => (
              <span
                key={key}
                className={`${classes.catBtn} ${atRiskFilter === key ? classes.catBtnOn : ''}`}
                onClick={() => setAtRiskFilter(key)}
              >
                {label}
                <span className={classes.catCount}>{n}</span>
              </span>
            ))}
          </Box>
        )}
        {/* Autopay filter — shows in ANY lane that has autopay parents (user
            2026-08-02): All / Autopay / No autopay. */}
        {autopayCount > 0 && (
          <Box className={classes.catBar} style={{ marginTop: 0, marginBottom: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#9aa7b3', width: 56, flexShrink: 0 }}>Autopay</span>
            {[['all', 'All', allRows.length], ['yes', 'Autopay', autopayCount], ['no', 'No autopay', allRows.length - autopayCount]].map(([key, label, n]) => (
              <span
                key={key}
                className={`${classes.catBtn} ${autopayFilter === key ? classes.catBtnOn : ''}`}
                onClick={() => setAutopayFilter(key)}
              >
                {label}
                <span className={classes.catCount}>{n}</span>
              </span>
            ))}
          </Box>
        )}
        {/* Sort control — Active afterschool only (that's where coins live). */}
        {activeLane === 'active' && category === 'afterschool' && (
          <Box className={classes.catBar} style={{ marginTop: 0, marginBottom: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#9aa7b3', width: 56, flexShrink: 0 }}>Sort</span>
            {[['name', 'Name (A–Z)'], ['coins_expiry', 'Coins expiry (soonest first)']].map(([k, lbl]) => (
              <span
                key={k}
                className={`${classes.catBtn} ${activeSort === k ? classes.catBtnOn : ''}`}
                onClick={() => setActiveSort(k)}
              >
                {lbl}
              </span>
            ))}
          </Box>
        )}
        {rows.length === 0 ? (
          <Box className={classes.empty}>
            Nothing in {(meta.label || lane).toLowerCase()} right now
            {` for ${CATEGORY_LABEL[category]}`}.
          </Box>
        ) : (
          <>
            {(showAllLanes[lane] ? rows : rows.slice(0, LANE_CAP)).map(renderRow)}
            {rows.length > LANE_CAP && (
              <div
                className={classes.showAll}
                onClick={() => setShowAllLanes((s) => ({ ...s, [lane]: !s[lane] }))}
              >
                {showAllLanes[lane]
                  ? `Show just the top ${LANE_CAP}`
                  : `Showing the top ${LANE_CAP} ${
                      // At Risk is ranked by how close the parent is to
                      // stopping, not by rupees — saying "by value" here would
                      // describe the wrong list.
                      lane === 'at_risk' ? 'to call first' : 'by value'
                    } — show all ${rows.length}`}
              </div>
            )}
          </>
        )}
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        {/* Page title removed on user's ask (2026-07-30) — the page needs no name. */}
        <GuideButton title="Guide">
          <p style={{ marginTop: 0 }}>
            Who to contact today at this centre, most urgent first. Pick a programme
            (Afterschool / Preschool / Toddler Club); each has its own lanes as tabs. Work down
            a lane — it is meant to be finishable.
          </p>
          <p>
            <b>Active</b> is the first tab in every programme — everyone enrolled and active in
            it this month, one row per parent (their subjects merged). It is the roster the
            other lanes are carved out of, not a call list, so it is simply alphabetical.
          </p>
          <p>
            <b>At risk.</b> Paying now, but something is wrong: the wallet has run out,
            attendance has dropped, an instalment is overdue, or a recent note sounds unhappy.
            Money we already have and are about to lose, so it leads. Each row shows the next
            action and the parent's actual last note. Filter chips narrow it by trigger.
          </p>
          <p>
            <b>Grow.</b> New parents who <i>visited</i> — a tour, trial or event — and have not
            joined yet. (Not existing parents; upsell lives elsewhere.) Preschool Grow is rated
            <b> High / Medium / Low</b> from the parent's interactions, with{' '}
            <b>“not logged”</b> on top — a fresh visitor nobody has followed up is the first
            thing to fix. Most urgent first.
          </p>
          <p>
            <b>Winback.</b> Inactive this month but winnable. The default is <i>every</i>{' '}
            inactive parent, ordered by coins expiring soonest; the chips (how long lapsed,
            coins held, coins expiry, subject) narrow it — one chip at a time.
          </p>
          <p>
            <b>Churned</b> (Afterschool). Parents who told us they've left or aren't interested.
            A record of who went and why — not a call list.
          </p>
          <p>
            <b>Pipeline</b> (Preschool only). Families you're working towards a preschool
            admission. You build it by hand — <b>Add to pipeline</b> shows on Preschool Grow,
            Toddler Club Grow and active Toddler Club rows, since a Toddler Club family is the
            biggest preschool feeder. Each is then rated High / Medium / Low automatically from
            interactions — never the child's age.
          </p>
          <p>
            <b>The “why”.</b> Each row shows a one-line summary of the last real conversation —
            the point of it (“already enrolled elsewhere”, “will start in June”), not a stock
            label — so you know what you're walking into before you call.
          </p>
          <p>
            <b>The order.</b> At risk is worked in steps — the parent who cannot book a single
            session next month comes before one who is merely short, whatever the amounts.
            Rupees only break a tie inside a step; the amount sits at the top of the interaction
            drawer, not on the row.
          </p>
          <p>
            <b>Up top:</b> <b>Follow-ups</b> (everyone who named a time to reconnect, most
            overdue first), <b>Pending chats</b> (parent messages awaiting a reply), and{' '}
            <b>New visitors — no interaction logged</b> (attended but never followed up).
          </p>
          <p>
            <b>Add the interaction.</b> The ordering, the follow-ups and the daily count are all
            built from what you log here.
          </p>
        </GuideButton>
        {/* Follow-ups / Pipeline / Centre / Month ride on the TITLE row (user
            2026-07-23) — they are what the team reaches for first, so they get
            the top line; the category filter drops to a row of its own. */}
        <Box style={{ marginLeft: 'auto', display: 'flex', gap: 12, alignItems: 'center' }}>
          {/* Centre Scoreboard — every centre's score /4 for a chosen day, worst
              first (user 2026-08-08). Cross-centre; drills into a centre's
              Focus. */}
          <Button
            size="small"
            variant={cockpitOpen ? 'contained' : 'outlined'}
            disableElevation
            className={cockpitOpen ? classes.panelBtnOn : classes.panelBtn}
            onClick={openCockpit}
          >
            📊 Centre Scoreboard
          </Button>
          {/* Focus today — the four daily must-dos, scored live. Sits with
              Follow-ups (user 2026-08-08): both are today's-action views that
              REPLACE the working list, not programme tabs. */}
          <Button
            size="small"
            variant={focusOpen ? 'contained' : 'outlined'}
            disableElevation
            className={focusOpen ? classes.panelBtnOn : classes.panelBtn}
            onClick={openFocus}
          >
            🎯 Focus today{focus?.score != null ? ` (${focus.score}/${focus.score_of})` : ''}
          </Button>
          {/* Trends — every centre's score /4 over the range, one graph each
              (user 2026-08-08). Reads the end-of-day score ledger + today live. */}
          <Button
            size="small"
            variant={trendsOpen ? 'contained' : 'outlined'}
            disableElevation
            className={trendsOpen ? classes.panelBtnOn : classes.panelBtn}
            onClick={openTrends}
          >
            📈 Trends
          </Button>
          <Button
            size="small"
            variant={followUpsOpen ? 'contained' : 'outlined'}
            disableElevation
            className={followUpsOpen ? classes.panelBtnOn : classes.panelBtn}
            onClick={() => {
              setFollowUpsOpen((v) => !v)
              setPipelineOpen(false)
              setEnrolledOpen(false)
              setChatsOpen(false)
              setIaOpen(false)
              setReportsOpen(false)
              setDcOpen(false)
              setFocusOpen(false)
              setTrendsOpen(false)
              setCockpitOpen(false)
            }}
          >
            Follow-ups{data?.follow_ups?.count ? ` (${data.follow_ups.count})` : ''}
            {data?.follow_ups?.overdue ? ` · ${data.follow_ups.overdue} overdue` : ''}
          </Button>
          {/* Top-level Pipeline button removed (user 2026-07-30) — Pipeline is
              now a preschool LANE, auto-built from TC + preschool visits. */}
          <FormControl variant="outlined" size="small" style={{ minWidth: 190 }}>
            <InputLabel>Centre</InputLabel>
            <Select
              label="Centre"
              value={centreUuid}
              onChange={(e) => setCentreUuid(e.target.value)}
            >
              {centres.map((c) => (
                <MenuItem key={c.centre_uuid} value={c.centre_uuid}>
                  {c.centre_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Month</InputLabel>
            <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
              {/* Current month + the last 11, most recent first — the worklist
                  and reports are reviewed for past months too (user 2026-07-31). */}
              {monthOptions(1, 11).reverse().map((m) => (
                <MenuItem key={m} value={m}>
                  {monthLabel(m)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* ⭐ TABS ARE PER PROGRAMME (Growth Toolkit §3.2, ruling 2026-07-24).
          This replaces the category FILTER that used to sit here. A centre
          person works one programme at a time, so the programme is the tab and
          the lanes live inside it — rather than one interleaved At Risk list
          with a filter across the top.

          "All active enrolled" is the first tab now, not a button that swaps
          the list out. Same four tabs her spec names. */}
      <Box className={classes.catBar} style={{ marginTop: 10 }}>
        <span
          className={`${classes.catBtn} ${enrolledOpen ? classes.catBtnOn : ''}`}
          onClick={openEnrolled}
        >
          All active enrolled
          {enrolled?.counts?.shown != null && (
            <span className={classes.catCount}>{enrolled.counts.shown}</span>
          )}
        </span>
        {/* Only the programmes this centre actually runs (user 2026-08-02):
            an afterschool-only centre (Salt Lake, Theatre Road) hides
            Preschool/Toddler Club. Falls back to all tabs while data loads. */}
        {PROGRAMME_TABS.filter((c) => (data?.programmes || PROGRAMME_TABS).includes(c)).map((c) => (
          <span
            key={c}
            className={`${classes.catBtn} ${
              !focusOpen && !trendsOpen && !cockpitOpen && !enrolledOpen && !chatsOpen && !iaOpen && !reportsOpen && !followUpsOpen && !pipelineOpen && !dcOpen && category === c ? classes.catBtnOn : ''
            }`}
            onClick={() => {
              setCategory(c)
              setEnrolledOpen(false)
              setChatsOpen(false)
              setIaOpen(false)
              setReportsOpen(false)
              setFollowUpsOpen(false)
              setPipelineOpen(false)
              setDcOpen(false)
              setFocusOpen(false)
              setTrendsOpen(false)
              setCockpitOpen(false)
            }}
          >
            {CATEGORY_LABEL[c]}
          </span>
        ))}
        {/* Pending centre-parent chats — a chat list, sibling to the enrolment
            directory (user 2026-07-29). */}
        <span
          className={`${classes.catBtn} ${chatsOpen ? classes.catBtnOn : ''}`}
          onClick={openChats}
        >
          Pending centre parent chats
          {chats?.counts?.all != null && (
            <span className={classes.catCount}>{chats.counts.all}</span>
          )}
        </span>
        {/* Interaction audit — prospects visited but not yet contacted
            (user 2026-07-30). */}
        <span
          className={`${classes.catBtn} ${iaOpen ? classes.catBtnOn : ''}`}
          onClick={openIa}
        >
          New visitors — no interaction logged
          {ia?.counts?.all != null && (
            <span className={classes.catCount}>{ia.counts.all}</span>
          )}
        </span>
        {/* Reports — a centre-wide, month-scoped view, sibling to the other
            support lists (user 2026-07-31). */}
        <span
          className={`${classes.catBtn} ${reportsOpen ? classes.catBtnOn : ''}`}
          onClick={() => {
            const next = !reportsOpen
            setReportsOpen(next)
            setEnrolledOpen(false)
            setChatsOpen(false)
            setIaOpen(false)
            setFollowUpsOpen(false)
            setPipelineOpen(false)
            setDcOpen(false)
            setFocusOpen(false)
            setTrendsOpen(false)
            setCockpitOpen(false)
          }}
        >
          Reports
        </span>
        {/* Daily Connected — a team-INPUT view (day-scoped, per person), sibling
            to Reports but not under it (Reports is month-scoped conversion). */}
        <span
          className={`${classes.catBtn} ${dcOpen ? classes.catBtnOn : ''}`}
          onClick={openDc}
        >
          Daily connected
        </span>
      </Box>
      {/* Daycare/Other "not covered" note removed on user's ask (2026-07-28). */}

      {/* Focus for the day — the layer over Grow & Retain. Four daily targets
          scored live, ranked by cost of inaction. Each target reuses an existing
          surface (chats / audit / follow-ups / daily-connected) so the numbers
          always match those tabs. */}
      {focusOpen && (
        <Box className={classes.pipelinePanel}>
          <div className={classes.pipelineHead}>
            <b>Focus for the day{data?.centre ? ` — ${data.centre}` : ''}</b>
            {focus && (
              <span className={classes.focusScore}>
                <span className={classes.focusScoreNum}>{focus.score}</span>
                <span className={classes.focusScoreOf}>/ {focus.score_of} done today</span>
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#8a96a3', marginTop: 4, lineHeight: 1.5 }}>
            The must-do list on top of Grow &amp; Retain — clear these every day.
            Ranked by what costs most if it slips. Each target opens the list it
            counts — the same tab, not a copy — so you act and log in one place.
          </div>
          {focusLoading && (
            <div style={{ padding: 18, textAlign: 'center', color: '#8493a5', fontSize: 13 }}>Loading…</div>
          )}
          {!focusLoading && focus && (
            <div style={{ marginTop: 12 }}>
              {/* Each target JUMPS to the existing tab that owns its list
                  (user 2026-08-08 — "we already have the lists, don't create
                  multiple lists for what exists"). No rows, no tel: links, no
                  re-opening the sheet. Connect has no single tab — it's the
                  programme lanes — so it stays a summary with its legs. */}
              {focus.targets.map((t) => {
                const jump = {
                  chats: { fn: openChats, tab: 'Pending chats' },
                  visitors: { fn: openIa, tab: 'New visitors' },
                  fups: { fn: openFollowUps, tab: 'Follow-ups' }
                }[t.key]
                const accent = t.hit
                  ? '#2e9e5b'
                  : t.severity === 'money' ? '#c9821a'
                    : t.severity === 'acquire' ? '#1565C0'
                      : '#d64545'
                const pill = t.hit
                  ? { text: t.goal_kind === 'quota' ? 'Quota met ✓' : 'Clear ✓', bg: '#e7f5ec', fg: '#1f7a45' }
                  : t.goal_kind === 'quota'
                    ? { text: `${t.open}/${t.target}`, bg: '#fdf1df', fg: '#9a5b0a' }
                    : { text: `${t.open} open`, bg: '#fdeaea', fg: '#b23838' }
                const canJump = !!jump && t.open > 0
                return (
                  <Box
                    key={t.key}
                    className={classes.focusTarget}
                    style={{ borderLeftColor: accent, ...(canJump ? { cursor: 'pointer' } : {}) }}
                    onClick={canJump ? jump.fn : undefined}
                  >
                    <div className={classes.focusTargetHead}>
                      <span className={classes.focusTargetTitle}>{t.label}</span>
                      <span className={classes.focusPill} style={{ background: pill.bg, color: pill.fg }}>
                        {pill.text}
                      </span>
                      {canJump && (
                        <span style={{ color: '#1565C0', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          Open {jump.tab} →
                        </span>
                      )}
                    </div>
                    <div className={classes.focusWhy}>
                      {t.why}
                      {t.aside ? ` · ${t.aside}` : ''}
                    </div>
                    {t.legs && (
                      <div className={classes.focusLegs}>
                        {t.legs.map((l) => {
                          // Prospect / daycare connects carry no goal of their
                          // own but still count toward the quota, so they read
                          // as a bare neutral count rather than a met/missed
                          // x/y (`l.done >= null` would score them green).
                          const noGoal = l.goal === null || l.goal === undefined
                          const met = !noGoal && l.done >= l.goal
                          const tone = noGoal
                            ? { borderColor: '#dbe3ec', background: '#f2f5f9', color: '#48586a' }
                            : met
                              ? { borderColor: '#bfe3cc', background: '#eef8f1', color: '#1f7a45' }
                              : { borderColor: '#f0d9b8', background: '#fdf6ec', color: '#9a5b0a' }
                          return (
                            <span
                              key={l.programme}
                              className={classes.focusLeg}
                              style={tone}
                            >
                              {l.label} <b>{l.done}</b>{noGoal ? '' : `/${l.goal}`}
                            </span>
                          )
                        })}
                      </div>
                    )}
                  </Box>
                )
              })}
            </div>
          )}
        </Box>
      )}

      {/* Trends — Focus over time for this centre. The connect input is the only
          target with real day-by-day history read-only; the stock targets ride
          along as today's standing (user 2026-08-08). */}
      {/* Centre Scoreboard — every in-scope centre's score /4 for the picked day,
          worst first. Today is live; a past date reads the score ledger. Click a
          row to switch to that centre. */}
      {cockpitOpen && (() => {
        const sCol = (s) => (s == null ? '#9aa7b3' : s >= 4 ? '#1f7a45' : s >= 2 ? '#9a5b0a' : '#b23838')
        const sBg = (s) => (s == null ? '#f2f5f9' : s >= 4 ? '#e7f5ec' : s >= 2 ? '#fbeecf' : '#fbe4e4')
        const nCol = (v, warn, crit) => (v == null ? '#9aa7b3' : v === 0 ? '#8a96a3' : v <= warn ? '#9a5b0a' : '#b23838')
        const connCol = (v) => (v == null ? '#9aa7b3' : v >= 40 ? '#1f7a45' : v >= 20 ? '#9a5b0a' : '#b23838')
        const rows = cockpit?.rows || []
        return (
          <Box className={classes.pipelinePanel}>
            <div className={classes.pipelineHead}>
              <b>Centre Scoreboard</b>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#48586a' }}>
                Date
                <input
                  type="date"
                  value={cockpitDate}
                  max={localDateStr()}
                  onChange={(e) => { setCockpitDate(e.target.value); loadCockpit(e.target.value) }}
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cdd6e0' }}
                />
              </span>
              {cockpit && (
                <span style={{ marginLeft: 'auto', fontSize: 12, color: '#8a96a3' }}>
                  {cockpit.is_today
                    ? 'live · today'
                    : cockpit.source === 'ledger'
                      ? 'recorded score'
                      : 'rebuilt from that day'}{' '}· worst first
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#8a96a3', marginTop: 4, lineHeight: 1.5 }}>
              One score out of 4 per centre — chats · visitors · follow-ups · connect. Click a centre to open it.
            </div>
            {cockpitLoading && (
              <div style={{ padding: 18, textAlign: 'center', color: '#8493a5', fontSize: 13 }}>Loading…</div>
            )}
            {!cockpitLoading && cockpit && rows.length === 0 && (
              <div style={{ padding: 16, color: '#8493a5', fontSize: 13, lineHeight: 1.6 }}>
                No centres are in scope for you, so there is nothing to score for this date.
              </div>
            )}
            {!cockpitLoading && rows.length > 0 && (
              <div style={{ overflowX: 'auto', marginTop: 10 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#8a96a3', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      <th style={{ padding: '6px 10px' }}>Centre</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center' }}>Score</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center' }}>Open chats</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center' }}>Visitors to log</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center' }}>Callbacks due</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center' }}>Connected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.centre_uuid}
                        onClick={() => {
                          if (r.centre_uuid === centreUuid) { openFocus(); return }
                          setPendingFocus(true)
                          setCentreUuid(r.centre_uuid)
                        }}
                        style={{ borderTop: '1px solid #eef2f6', cursor: 'pointer' }}
                      >
                        <td style={{ padding: '9px 10px', fontWeight: 600 }}>{r.centre}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                          <span style={{ fontWeight: 700, color: sCol(r.score), background: sBg(r.score), padding: '2px 9px', borderRadius: 8 }}>
                            {r.score == null ? '—' : `${r.score}/${r.score_of}`}
                          </span>
                        </td>
                        <td style={{ padding: '9px 10px', textAlign: 'center', fontWeight: 700, color: nCol(r.chats_open, 3) }}>{r.chats_open == null ? '—' : r.chats_open}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'center', fontWeight: 700, color: nCol(r.visitors_open, 2) }}>{r.visitors_open == null ? '—' : r.visitors_open}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'center', fontWeight: 700, color: nCol(r.fups_overdue, 10) }}>{r.fups_overdue == null ? '—' : r.fups_overdue}</td>
                        <td style={{ padding: '9px 10px', textAlign: 'center', fontWeight: 700, color: connCol(r.connect_done) }}>
                          {r.connect_done == null ? '—' : `${r.connect_done}/${r.connect_goal || 40}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Box>
        )
      })()}

      {/* Trends — every centre's score /4 over the range, one small graph each.
          Reads the end-of-day score ledger; today's point is live. */}
      {trendsOpen && (() => {
        const centres = trends?.centres || []
        const sCol = (s) => (s >= 4 ? '#1f7a45' : s >= 2 ? '#9a5b0a' : '#b23838')
        const W = 190; const H = 46; const PAD = 4; const MAXS = 4
        const chart = (days) => {
          if (!days.length) return null
          const n = days.length
          const x = (i) => PAD + (n === 1 ? (W - PAD * 2) / 2 : (i * (W - PAD * 2)) / (n - 1))
          const y = (v) => H - PAD - (v / MAXS) * (H - PAD * 2)
          const pts = days.map((d, i) => `${x(i).toFixed(1)},${y(d.score).toFixed(1)}`).join(' ')
          const grid = [0, 2, 4].map((g) => (
            <line key={g} x1="0" x2={W} y1={y(g)} y2={y(g)} stroke="#eef2f6" strokeWidth="1" />
          ))
          const last = n - 1
          return (
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 54, overflow: 'visible' }}>
              {grid}
              <polyline points={pts} fill="none" stroke="#3f4fd6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {days.map((d, i) => (
                <circle key={d.date} cx={x(i).toFixed(1)} cy={y(d.score).toFixed(1)} r={i === last ? 3.2 : 1.7} fill={i === last ? '#3f4fd6' : '#9aa7b3'} />
              ))}
            </svg>
          )
        }
        return (
          <Box className={classes.pipelinePanel}>
            <div className={classes.pipelineHead}>
              <b>Trends — score /4 by centre</b>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#48586a' }}>
                From
                <input type="date" value={trendStart} max={trendEnd}
                  onChange={(e) => { setTrendStart(e.target.value); loadTrends(e.target.value, trendEnd) }}
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cdd6e0' }} />
                to
                <input type="date" value={trendEnd} min={trendStart} max={localDateStr()}
                  onChange={(e) => { setTrendEnd(e.target.value); loadTrends(trendStart, e.target.value) }}
                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cdd6e0' }} />
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#8a96a3', marginTop: 4, lineHeight: 1.5 }}>
              Each centre&apos;s daily score out of 4 over the range. Today&apos;s point is live; earlier points
              come from the end-of-day score record, so history fills in one day at a time once it runs.
            </div>
            {trendsLoading && (
              <div style={{ padding: 18, textAlign: 'center', color: '#8493a5', fontSize: 13 }}>Loading…</div>
            )}
            {!trendsLoading && trends && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12, marginTop: 12 }}>
                {centres.map((c) => {
                  const last = c.days.length ? c.days[c.days.length - 1] : null
                  return (
                    <div key={c.centre_uuid} style={{ border: '1px solid #e4eaf1', borderRadius: 12, background: '#fff', padding: '11px 13px 8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                        <span style={{ fontWeight: 700, fontSize: 13.5 }}>{c.centre}</span>
                        {last && <span style={{ fontSize: 12, fontWeight: 700, color: sCol(last.score) }}>{last.score}/4 today</span>}
                      </div>
                      {c.days.length ? chart(c.days) : (
                        <div style={{ fontSize: 11.5, color: '#9aa7b3', padding: '14px 0' }}>No history yet</div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </Box>
        )
      })()}

      {/* The to-collect / days-left / % secured strip used to sit here. Removed
          on Shruti's ask (2026-07-24): the target belongs to the Set Targets
          and board pages, and on a worklist it was a number nobody could act
          on sitting above the rows they can. The lane tabs already carry the
          counts. */}

      {/* Every promised call-back in one place, ordered by cadence: most
          overdue at the top, then today, then soonest. Scoped by the parent's
          centre rather than by the lanes, so a promise to a currently-healthy
          parent still shows — that is the case the lanes would have hidden. */}
      {reportsOpen && (
        <Box style={{ marginTop: 14 }}>
          <ReportsPanel centreUuid={centreUuid} month={month} />
        </Box>
      )}

      {dcOpen && (
        <Box className={classes.pipelinePanel}>
          <div className={classes.pipelineHead}>
            <b>Daily connected — how many parents each person reached</b>
            <input
              type="date"
              value={dcDate}
              max={localDateStr()}
              onChange={(e) => { setDcDate(e.target.value); setDcOpenPerson(null); loadDc(e.target.value) }}
              style={{ marginLeft: 12, padding: '4px 8px', borderRadius: 6, border: '1px solid #cdd6e0' }}
            />
            {dc && (
              <span style={{ marginLeft: 12, color: '#48586a', fontSize: 13 }}>
                <b>{dc.total_connected}</b> connected · {dc.people_count} people · {dc.connect_rate}% connect-rate
              </span>
            )}
          </div>
          {/* Plain-English notes so the numbers read without asking. */}
          <div style={{ fontSize: 12, color: '#8a96a3', marginTop: 4, lineHeight: 1.5 }}>
            <b>Connected</b> = parent was reached · <b>Attempts</b> = calls logged that day
            (reached + didn&apos;t pick up) · <b>Connect rate</b> = reached ÷ attempts.
            Reflects only what the team <b>logs in the CRM</b>. Every cohort is listed —
            a <b>0</b> means nobody in it was reached that day. Click a <b>cohort</b> below to
            filter; click a <b>person</b> to see who they reached.
          </div>
          {dcLoading ? (
            <Box className={classes.empty} style={{ marginTop: 10 }}>Loading…</Box>
          ) : !dc || !dc.total_connected ? (
            <Box className={classes.empty} style={{ marginTop: 10 }}>
              No connected calls logged on this day.
            </Box>
          ) : (
            <>
              {/* Programme + cohort split, as CLICKABLE filter chips (user
                  2026-08-07). "All" resets; a cohort chip (e.g. Afterschool ·
                  Winback) filters the people + drill-down to that cohort's
                  connected calls. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0 4px' }}>
                <span
                  onClick={() => { setDcCohort('all'); setDcOpenPerson(null) }}
                  className={classes.chip}
                  style={{
                    cursor: 'pointer',
                    ...(dcCohort === 'all'
                      ? { background: '#0f7a56', color: '#fff' }
                      : { background: '#eef2f6', color: '#48586a' })
                  }}
                >
                  All {dc.total_connected}
                </span>
                {dc.programmes.flatMap((pr) => pr.cohorts.map((c) => {
                  const key = `${pr.programme}:${c.cohort}`
                  const on = dcCohort === key
                  const base = CAT_CHIP[pr.programme] || CAT_CHIP.other
                  // Every cohort is listed, including the ones nobody called
                  // — a 0 is the signal (user 2026-08-21). Muted so the eye
                  // still lands on the cohorts that were actually worked.
                  const tone = on
                    ? { background: base.color, color: '#fff' }
                    : c.count ? base : { background: '#f7f9fb', color: '#9aa7b3' }
                  return (
                    <span
                      key={key}
                      onClick={() => { setDcCohort(on ? 'all' : key); setDcOpenPerson(null) }}
                      className={classes.chip}
                      style={{ cursor: 'pointer', ...tone }}
                    >
                      {pr.label} · {c.label} {c.count}
                    </span>
                  )
                }))}
              </div>
              {dcCohort !== 'all' && (() => {
                const any = dc.people.some((p) => p.rows.some(
                  (r) => `${r.programme}:${r.cohort}` === dcCohort))
                return (
                  <>
                    <div style={{ fontSize: 12, color: '#8a96a3', margin: '2px 0 8px' }}>
                      Showing only connected calls to this cohort. (Attempts / connect-rate
                      are call-level, so they aren&apos;t split by cohort.)
                    </div>
                    {/* A 0 cohort is selectable like any other; say so rather
                        than rendering an empty gap. */}
                    {!any && (
                      <Box className={classes.empty} style={{ marginTop: 4 }}>
                        Nobody in this cohort was reached on this day.
                      </Box>
                    )}
                  </>
                )
              })()}
              {/* Per-person rows — filtered to the chosen cohort. Click to expand. */}
              {dc.people.map((p) => {
                const rows = dcCohort === 'all'
                  ? p.rows
                  : p.rows.filter((r) => `${r.programme}:${r.cohort}` === dcCohort)
                const connected = dcCohort === 'all' ? p.connected : rows.length
                if (!connected) return null
                const parents = dcCohort === 'all'
                  ? p.parents_connected
                  : new Set(rows.map((r) => r.parent_user_uuid)).size
                return (
                  <Box key={p.staff_uuid} className={classes.row} style={{ display: 'block', marginTop: 7 }}>
                    <div
                      onClick={() => setDcOpenPerson(dcOpenPerson === p.staff_uuid ? null : p.staff_uuid)}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: 12 }}
                    >
                      <span className={classes.name}>{p.name}</span>
                      <span style={{ fontSize: 13, color: '#48586a', whiteSpace: 'nowrap' }}>
                        <b style={{ color: '#0f7a56' }}>{connected}</b> connected · {parents} parents
                        {dcCohort === 'all'
                          ? ` · ${p.connect_rate}% connect-rate · ${p.attempts} attempts`
                          : ''}
                      </span>
                    </div>
                    {dcOpenPerson === p.staff_uuid && (
                      <div style={{ marginTop: 4, paddingLeft: 10, borderLeft: '2px solid #eef2f6' }}>
                        {rows.map((r, i) => (
                          <Box key={i} className={classes.row} style={{ marginTop: 5 }}>
                            <span className={classes.who}>
                              <a
                                href={`/home/parent?parent=${encodeURIComponent(r.parent_user_uuid)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#1565C0', textDecoration: 'none' }}
                              >
                                <span className={classes.name}>{r.parent_name}</span>
                              </a>
                              <div className={classes.last} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span className={classes.chip} style={CAT_CHIP[r.programme] || CAT_CHIP.other}>
                                  {r.programme_label} · {r.cohort_label}
                                </span>
                                {r.mode ? <span style={{ color: '#8a96a3' }}>{r.mode}</span> : null}
                              </div>
                            </span>
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              style={{ textTransform: 'none' }}
                              onClick={() => setDrawerParent({ uuid: r.parent_user_uuid, name: r.parent_name, phone: '' })}
                            >
                              Add interaction
                            </Button>
                          </Box>
                        ))}
                      </div>
                    )}
                  </Box>
                )
              })}
            </>
          )}
        </Box>
      )}

      {!loading && followUpsOpen && (
        <Box className={classes.pipelinePanel}>
          <Box className={classes.pipelineHead}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Follow-ups</span>
            <Box className={classes.laneTabs} style={{ margin: 0 }}>
              {[
                ['all', `All ${data?.follow_ups?.count || 0}`],
                ['overdue', `Overdue ${data?.follow_ups?.overdue || 0}`],
                ['today', `Today ${data?.follow_ups?.due_today || 0}`],
                ['upcoming', `Upcoming ${data?.follow_ups?.upcoming || 0}`]
              ].map(([key, label]) => (
                <span
                  key={key}
                  className={`${classes.laneTab} ${followUpFilter === key ? classes.laneTabOn : ''}`}
                  onClick={() => setFollowUpFilter(key)}
                >
                  {label}
                </span>
              ))}
            </Box>
            <span className={classes.calendarSoon}>📅 Calendar sync coming soon</span>
          </Box>
          {/* Counts the rows CURRENTLY IN VIEW, not the whole centre — picking
              Overdue and then seeing every category's full total was reading as
              a bifurcation of 97 when it was a bifurcation of 101. */}
          {catStrip(
            (data?.follow_ups?.entries || []).filter(
              (e) => followUpFilter === 'all' || e.state === followUpFilter
            ),
            followUpCat,
            setFollowUpCat
          )}

          {(() => {
            const rows = (data?.follow_ups?.entries || []).filter(
              (e) =>
                (followUpFilter === 'all' || e.state === followUpFilter) &&
                inCategory(e, followUpCat)
            )
            if (!rows.length) {
              return (
                <Box className={classes.empty} style={{ marginTop: 10 }}>
                  {(data?.follow_ups?.count || 0) === 0
                    ? 'No follow-ups yet. They appear automatically when a parent names a time to call back in a logged interaction.'
                    : 'Nothing in this group.'}
                </Box>
              )
            }
            return rows.map((e) => (
              <Box className={classes.row} key={e.parent_user_uuid} style={{ marginTop: 7 }}>
                <span
                  className={classes.chip}
                  style={
                    e.state === 'overdue'
                      ? { background: '#fceaea', color: '#cf3a3a' }
                      : e.state === 'today'
                        ? { background: '#fbefdb', color: '#b06800' }
                        : undefined
                  }
                >
                  {e.state === 'overdue'
                    ? `${Math.abs(e.days)}d overdue`
                    : e.state === 'today'
                      ? 'today'
                      : `in ${e.days}d`}
                </span>
                <span className={classes.who}>
                  <a
                    href={`/home/parent?parent=${encodeURIComponent(e.parent_user_uuid)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1565C0', textDecoration: 'none' }}
                  >
                    <span className={classes.name}>{e.parent_name}</span>
                  </a>
                  {/* Lead with WHAT to follow up for — the crux of the note that
                      named the date (user 2026-07-30) — with the topic tag and
                      the dates underneath as context. */}
                  <div className={classes.reason}>
                    {e.topic ? (
                      <span
                        className={classes.chip}
                        style={{ marginRight: 6, background: '#eef2f7', color: '#48586a' }}
                      >
                        {e.topic}
                      </span>
                    ) : null}
                    {e.note || `Follow up by ${dayMonthYear(e.follow_up_date)}`}
                  </div>
                  <div className={classes.last}>
                    Due <b>{dayMonthYear(e.follow_up_date)}</b>
                    {e.promised_on ? ` · last contacted ${dayMonthYear(e.promised_on)}` : ''}
                  </div>
                </span>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  style={{ textTransform: 'none' }}
                  onClick={() =>
                    setDrawerParent({
                      uuid: e.parent_user_uuid,
                      name: e.parent_name,
                      phone: e.phone
                    })
                  }
                >
                  Add interaction
                </Button>
              </Box>
            ))
          })()}
        </Box>
      )}

      {/* The pipeline in one place, filterable by who put each parent there —
          two people can be working the same centre's list. */}
      {!loading && pipelineOpen && (
        <Box className={classes.pipelinePanel}>
          <Box className={classes.pipelineHead}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              Pipeline · {(pipeline?.entries || []).filter(
                (e) =>
                  (!pipelineAdder || e.added_by_uuid === pipelineAdder) &&
                  inCategory(e, pipelineCat)
              ).length} parent(s)
            </span>
            <Box className={classes.laneTabs} style={{ margin: 0 }}>
              <span
                className={`${classes.laneTab} ${!pipelineAdder ? classes.laneTabOn : ''}`}
                onClick={() => setPipelineAdder('')}
              >
                Everyone
              </span>
              {(pipeline?.adders || []).map((a) => (
                <span
                  key={a.uuid}
                  className={`${classes.laneTab} ${pipelineAdder === a.uuid ? classes.laneTabOn : ''}`}
                  onClick={() => setPipelineAdder(a.uuid)}
                >
                  {a.name}
                </span>
              ))}
            </Box>
          </Box>
          {/* Same rule as Follow-ups: count what the adder filter has left. */}
          {catStrip(
            (pipeline?.entries || []).filter(
              (e) => !pipelineAdder || e.added_by_uuid === pipelineAdder
            ),
            pipelineCat,
            setPipelineCat
          )}
          {(pipeline?.entries || []).filter(
            (e) =>
              (!pipelineAdder || e.added_by_uuid === pipelineAdder) &&
              inCategory(e, pipelineCat)
          ).length === 0 ? (
            <Box className={classes.empty} style={{ marginTop: 10 }}>
              Nobody in the pipeline yet. Use <b>Add to pipeline</b> on any row.
            </Box>
          ) : (
            (pipeline?.entries || [])
              .filter(
                (e) =>
                  (!pipelineAdder || e.added_by_uuid === pipelineAdder) &&
                  inCategory(e, pipelineCat)
              )
              .map((e) => (
                <Box className={classes.row} key={e.parent_user_uuid} style={{ marginTop: 7 }}>
                  <span className={classes.who}>
                    <a
                      href={`/home/parent?parent=${encodeURIComponent(e.parent_user_uuid)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1565C0', textDecoration: 'none' }}
                    >
                      <span className={classes.name}>{e.parent_name}</span>
                    </a>
                    {/* Tentative joining month + categories interested in, as
                        chips (user 2026-08-07) — the two judgements the pickup
                        was made on. Grade/subjects ride along as the specifics. */}
                    {(e.tentative_month ||
                      (e.categories || []).some((c) => c && c !== 'other') ||
                      e.grade ||
                      e.subjects) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 3 }}>
                        {e.tentative_month && (
                          <span
                            className={classes.chip}
                            style={{ background: '#fff4e5', color: '#B26A00' }}
                          >
                            Joining ~ {monthLabel(e.tentative_month)}
                          </span>
                        )}
                        {(e.categories || [])
                          .filter((c) => c && c !== 'other')
                          .map((c) => (
                            <span
                              key={c}
                              className={classes.chip}
                              style={CAT_CHIP[c] || CAT_CHIP.other}
                            >
                              {CATEGORY_LABEL[c] || c}
                            </span>
                          ))}
                        {e.grade && <span className={classes.chip}>{e.grade}</span>}
                        {(e.subjects || '')
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean)
                          .map((s) => (
                            <span key={s} className={classes.chip}>
                              {s}
                            </span>
                          ))}
                      </div>
                    )}
                    <div className={classes.last}>
                      Added by {e.added_by || 'someone'} on {dayMonthYear(e.added_on)}
                    </div>
                  </span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={pipelineBusy === e.parent_user_uuid}
                    style={{ textTransform: 'none' }}
                    onClick={() => removePipeline(e.parent_user_uuid)}
                  >
                    Remove
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    style={{ textTransform: 'none' }}
                    onClick={() =>
                      setDrawerParent({ uuid: e.parent_user_uuid, name: e.parent_name, phone: '' })
                    }
                  >
                    Add interaction
                  </Button>
                </Box>
              ))
          )}
        </Box>
      )}

      {loading && (
        <Box style={{ padding: 40, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Follow-ups and Pipeline REPLACE the working list rather than opening
          above it. Inserting them pushed the lanes off the screen, so opening a
          reference list cost you your place in the one you were working. */}

      {/* Categories whose logic isn't built yet say so, rather than showing an
          empty table that just reads as broken (user ask 2026-07-21). */}
      {enrolledOpen && (
        <Box className={classes.pipelinePanel}>
          <Box className={classes.pipelineHead}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              All active enrolled parents
              {enrolled?.counts?.shown != null ? ` · ${enrolled.counts.shown}` : ''}
            </span>
            <input
              value={enrolledSearch}
              onChange={(e) => setEnrolledSearch(e.target.value)}
              placeholder="Search name, programme, batch…"
              style={{
                marginLeft: 'auto', minWidth: 260, padding: '6px 10px',
                border: '1px solid #d9e1e9', borderRadius: 4, fontSize: 13,
                outline: 'none', fontFamily: 'inherit'
              }}
            />
          </Box>
          {/* Programme filter (Shruti, 2026-07-24). Built from what this centre
              actually has rather than a fixed list, so an empty programme does
              not offer a tab that leads to nothing. Counts are of active
              enrolments, matching the rows below. */}
          {!enrolledLoading && (enrolled?.rows || []).length > 0 && (
            <Box className={classes.catBar} style={{ marginTop: 8, marginBottom: 2 }}>
              {[
                ['all', 'All', (enrolled?.rows || []).length],
                ...Object.entries(enrolled?.type_counts || {})
                  .sort((a, b) => b[1] - a[1])
                  .map(([label, n]) => [label, label, n])
              ].map(([key, label, n]) => (
                <span
                  key={key}
                  className={`${classes.catBtn} ${enrolledType === key ? classes.catBtnOn : ''}`}
                  onClick={() => setEnrolledType(key)}
                >
                  {label}
                  <span className={classes.catCount}>{n}</span>
                </span>
              ))}
            </Box>
          )}
          {enrolledLoading ? (
            <Box className={classes.empty} style={{ marginTop: 10 }}>
              <CircularProgress size={18} /> &nbsp;Loading every active enrolment…
            </Box>
          ) : (() => {
            const q = enrolledSearch.trim().toLowerCase()
            const rows = (enrolled?.rows || []).filter(
              (r) =>
                (enrolledType === 'all' || r.enrollment_type === enrolledType) &&
                (!q ||
                  [r.parent_name, r.child_name, r.enrollment_type, r.grade, r.schedule, r.program, r.batch]
                    .some((v) => (v || '').toLowerCase().includes(q)))
            )
            if (!rows.length) {
              return (
                <Box className={classes.empty} style={{ marginTop: 10 }}>
                  {(enrolled?.rows || []).length
                    ? 'Nothing matches that search.'
                    : 'No active enrolments at this centre.'}
                </Box>
              )
            }
            return (
              <>
                {rows.slice(0, ENROLLED_CAP).map((r) => (
                  <Box
                    className={classes.row}
                    key={r.enrolment_uuid}
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <span className={classes.who} style={{ flex: 1, minWidth: 0 }}>
                      {/* Same markup as a lane row: name links to the parent's
                          profile, detail sits under it as a block. */}
                      <a
                        href={`/home/parent?parent=${encodeURIComponent(r.parent_user_uuid)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1565C0', textDecoration: 'none' }}
                      >
                        <span className={classes.name}>{r.parent_name}</span>
                      </a>
                      <div className={classes.reason}>
                        {/* category | day | child | subject (user 2026-08-02).
                            Preschool keeps the child's grade appended to the
                            subject — "Preschool" alone doesn't say the room. */}
                        {[
                          r.enrollment_type,
                          r.child_name,
                          r.enrollment_type === 'Preschool' && r.grade
                            ? `${r.program} (${r.grade})`
                            : r.program
                        ]
                          .filter(Boolean)
                          .join(' | ') || '—'}
                      </div>
                    </span>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      style={{ textTransform: 'none' }}
                      onClick={() =>
                        setDrawerParent({
                          uuid: r.parent_user_uuid,
                          name: r.parent_name,
                          phone: r.phone
                        })
                      }
                    >
                      Add interaction
                    </Button>
                  </Box>
                ))}
                {rows.length > ENROLLED_CAP && (
                  <div className={classes.showAll} style={{ cursor: 'default' }}>
                    Showing the first {ENROLLED_CAP} of {rows.length} — search to narrow it down
                  </div>
                )}
              </>
            )
          })()}
        </Box>
      )}

      {chatsOpen && (
        <Box className={classes.pipelinePanel}>
          <Box className={classes.pipelineHead}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              Pending centre parent chats
              {chats?.counts?.all != null ? ` · ${chats.counts.all} overdue` : ''}
            </span>
            <input
              value={chatsSearch}
              onChange={(e) => setChatsSearch(e.target.value)}
              placeholder="Search name or message…"
              style={{
                marginLeft: 'auto', minWidth: 260, padding: '6px 10px',
                border: '1px solid #d9e1e9', borderRadius: 4, fontSize: 13,
                outline: 'none', fontFamily: 'inherit'
              }}
            />
          </Box>
          {chatsLoading ? (
            <Box className={classes.empty} style={{ marginTop: 10 }}>
              <CircularProgress size={18} /> &nbsp;Loading open chats…
            </Box>
          ) : (() => {
            const q = chatsSearch.trim().toLowerCase()
            const rows = (chats?.rows || []).filter(
              (r) =>
                !q ||
                [r.parent_name, r.last_message].some((v) =>
                  (v || '').toLowerCase().includes(q)
                )
            )
            if (!rows.length) {
              return (
                <Box className={classes.empty} style={{ marginTop: 10 }}>
                  {(chats?.rows || []).length
                    ? 'Nothing matches that search.'
                    : 'No overdue chats — the centre is caught up.'}
                </Box>
              )
            }
            return rows.map((r) => (
              <Box
                className={classes.row}
                key={r.channel_uuid}
                style={{ display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <span className={classes.who} style={{ flex: 1, minWidth: 0 }}>
                  <a
                    href={`/home/parent?parent=${encodeURIComponent(r.parent_user_uuid)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1565C0', textDecoration: 'none' }}
                  >
                    <span className={classes.name}>{r.parent_name}</span>
                  </a>
                  <div className={classes.reason} style={{ fontStyle: 'italic', opacity: 0.85 }}>
                    {r.last_message ? `“${r.last_message}”` : '—'}
                  </div>
                  <div className={classes.last}>
                    {r.phone ? <span className={classes.rowPhone}>{r.phone}</span> : null}
                    {r.channel_type === 'PARENT_TEACHER' ? 'Teacher chat · ' : ''}
                    {r.waiting_hours != null
                      ? r.waiting_hours < 48
                        ? `overdue ${r.waiting_hours}h`
                        : `overdue ${Math.round(r.waiting_hours / 24)}d`
                      : ''}
                  </div>
                </span>
              </Box>
            ))
          })()}
        </Box>
      )}

      {iaOpen && (
        <Box className={classes.pipelinePanel}>
          <Box className={classes.pipelineHead}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              New visitors who attended — no interaction logged since
              {ia?.counts?.all != null ? ` · ${ia.counts.all}` : ''}
              {ia?.counts?.overdue ? ` · ${ia.counts.overdue} overdue` : ''}
            </span>
            <input
              value={iaSearch}
              onChange={(e) => setIaSearch(e.target.value)}
              placeholder="Search name…"
              style={{
                marginLeft: 'auto', minWidth: 260, padding: '6px 10px',
                border: '1px solid #d9e1e9', borderRadius: 4, fontSize: 13,
                outline: 'none', fontFamily: 'inherit'
              }}
            />
          </Box>
          {iaLoading ? (
            <Box className={classes.empty} style={{ marginTop: 10 }}>
              <CircularProgress size={18} /> &nbsp;Loading interaction audit…
            </Box>
          ) : (() => {
            const q = iaSearch.trim().toLowerCase()
            const rows = (ia?.rows || []).filter(
              (r) => !q || (r.name || '').toLowerCase().includes(q)
            )
            if (!rows.length) {
              return (
                <Box className={classes.empty} style={{ marginTop: 10 }}>
                  {(ia?.rows || []).length
                    ? 'Nothing matches that search.'
                    : 'Every attendee has an interaction logged — nothing pending.'}
                </Box>
              )
            }
            return rows.map((r) => (
              <Box
                className={classes.row}
                key={r.parent_user_uuid}
                style={{ display: 'flex', alignItems: 'center', gap: 12 }}
              >
                <span className={classes.who} style={{ flex: 1, minWidth: 0 }}>
                  <a
                    href={`/home/parent?parent=${encodeURIComponent(r.parent_user_uuid)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1565C0', textDecoration: 'none' }}
                  >
                    <span className={classes.name}>{r.name}</span>
                  </a>
                  <div className={classes.reason}>
                    {(r.segments || []).join(', ')}
                    {(r.segments || []).length ? ' — ' : ''}
                    <span
                      style={{
                        color: r.interactions_since_visit === 0 ? '#cf3a3a' : '#b06800',
                        fontWeight: 500
                      }}
                    >
                      {r.interactions_since_visit === 0
                        ? 'no interaction logged'
                        : `last logged ${r.days_since_interaction}d ago`}
                    </span>
                  </div>
                  <div className={classes.last}>
                    {r.phone ? <span className={classes.rowPhone}>{r.phone}</span> : null}
                    visited {r.days_since_visit}d ago
                    {r.spoc ? ` · ${r.spoc}` : ''}
                  </div>
                </span>
              </Box>
            ))
          })()}
        </Box>
      )}

      {!loading && !focusOpen && !trendsOpen && !cockpitOpen && !followUpsOpen && !pipelineOpen && !enrolledOpen && !chatsOpen && !iaOpen && !reportsOpen && !dcOpen && underConstruction && (
        <Box className={classes.construction}>
          <Typography style={{ fontWeight: 700, marginBottom: 6 }}>
            {CATEGORY_LABEL[category]} — under construction
          </Typography>
          <Typography style={{ color: '#48586a', fontSize: 14 }}>
            We haven&apos;t worked out the rules for {CATEGORY_LABEL[category]} yet, so nothing is
            ranked here. Afterschool and Preschool are live — use those tabs meanwhile.
          </Typography>
        </Box>
      )}

      {!loading && !focusOpen && !trendsOpen && !cockpitOpen && !followUpsOpen && !pipelineOpen && !enrolledOpen && !chatsOpen && !iaOpen && !reportsOpen && !dcOpen && !underConstruction && (
        <>
          {data?.partial_coverage?.[category] && (
            <Box className={classes.caveat}>
              <span>⚠</span>
              <span>
                <b>{CATEGORY_LABEL[category]} coverage is uneven.</b>{' '}
                {data.partial_coverage[category]}
              </span>
            </Box>
          )}
          {renderLaneTabs()}
          {/* Removed on Shruti's ask (2026-07-24), in the order she gave them:
              the lane-order caveat bar, the "this month: n of m contacted"
              outcome line, and the counted-only healthy / gone-quiet rows.
              Every one of them explained the page rather than being work on
              it. The lane ORDER is unchanged — only its description is gone,
              and it still lives in the explainer PDF and the tab tooltips.
              `lane_notes`, `outcomes` and `counted` are still returned by the
              API; nothing reads them here now. */}
          {renderActiveLane()}
        </>
      )}

      {/* Picking a parent up is a judgement — capture what it was based on. */}
      <Dialog open={Boolean(addForm)} onClose={() => setAddForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add {addForm?.row?.parent_name} to pipeline</DialogTitle>
        <DialogContent>
          <FormControl required variant="outlined" size="small" fullWidth style={{ marginTop: 4 }}>
            <InputLabel>Tentative month of joining</InputLabel>
            <Select
              label="Tentative month of joining"
              value={addForm?.tentative_month || ''}
              onChange={(e) => setAddForm((f) => ({ ...f, tentative_month: e.target.value }))}
            >
              {monthOptions(12).map((m) => (
                <MenuItem key={m} value={m}>
                  {monthLabel(m)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category is chosen, not inferred. It decides which list of
              interests appears underneath. */}
          <FormControl required variant="outlined" size="small" fullWidth style={{ marginTop: 14 }}>
            <InputLabel>Category</InputLabel>
            <Select
              multiple
              label="Category"
              value={addForm?.category || []}
              renderValue={(picked) =>
                (picked || []).map((c) => CATEGORY_LABEL[c]).join(', ')
              }
              onChange={(e) =>
                // Clear the interests when the category set changes: a
                // preschool grade means nothing once the parent is asking
                // about afterschool.
                setAddForm((f) => ({
                  ...f,
                  category: e.target.value,
                  grade: '',
                  subjects: [],
                  subjectsOther: ''
                }))
              }
            >
              {PIPELINE_CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={(addForm?.category || []).indexOf(c) > -1}
                  />
                  <ListItemText primary={CATEGORY_LABEL[c]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(addForm?.category || []).some((c) => GRADE_CATEGORIES.includes(c)) && (
            <FormControl required variant="outlined" size="small" fullWidth style={{ marginTop: 14 }}>
              <InputLabel>Grade interested in</InputLabel>
              <Select
                label="Grade interested in"
                value={addForm?.grade || ''}
                onChange={(e) => setAddForm((f) => ({ ...f, grade: e.target.value }))}
              >
                {GRADES.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Both can now show at once — a family asking about preschool AND
              afterschool needs a grade and a subject list, not one or the
              other. */}
          {(addForm?.category || []).some((c) => !GRADE_CATEGORIES.includes(c)) && (
            <FormControl required variant="outlined" size="small" fullWidth style={{ marginTop: 14 }}>
              <InputLabel>Subjects interested in</InputLabel>
              <Select
                multiple
                label="Subjects interested in"
                value={addForm?.subjects || []}
                onChange={(e) => setAddForm((f) => ({ ...f, subjects: e.target.value }))}
                renderValue={(picked) => (picked || []).join(', ')}
              >
                {/* Tick boxes, not a grey highlight — a multi-select that only
                    shades the chosen rows reads as "these are disabled". */}
                {(data?.afterschool_subjects || []).map((sub) => (
                  <MenuItem key={sub} value={sub}>
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={(addForm?.subjects || []).indexOf(sub) > -1}
                    />
                    <ListItemText primary={sub} />
                  </MenuItem>
                ))}
                {!(data?.afterschool_subjects || []).length && (
                  <MenuItem disabled value="">
                    No subjects on this centre&apos;s catalogue — type below
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          {/* Free text stays, whatever the category. The team records what a
              parent SAID, which is routinely not on the catalogue yet. */}
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            style={{ marginTop: 14 }}
            label="Anything else they asked for (optional)"
            placeholder="e.g. Robotics, Public Speaking"
            value={addForm?.subjectsOther || ''}
            onChange={(e) => setAddForm((f) => ({ ...f, subjectsOther: e.target.value }))}
          />
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          <Button
            onClick={submitAdd}
            variant="contained"
            color="primary"
            disableElevation
            disabled={Boolean(pipelineBusy) || pipelineMissing.length > 0}
            style={{ textTransform: 'none' }}
          >
            Add to pipeline
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logging a call changes the signals this page ranks on. Refetch at once
          so the row shows "last contacted today" and drops as actioned; then
          re-extract JUST this parent (a few LLM calls, unchanged notes skipped)
          and refetch again so the list re-ranks on what was actually logged —
          the "promised to pay", "not interested", new intent. Best-effort: if
          the extraction hiccups, the first refetch already updated the basics. */}
      <InteractionDrawer
        parent={drawerParent}
        onClose={() => setDrawerParent(null)}
        onLogged={async () => {
          const pid = drawerParent?.uuid
          load(month, centreUuid)
          if (!pid) return
          try {
            await extractParentSignals(pid, centreUuid)
          } catch (error) {
            // Swallowed — the first refetch already reflected last-contact and
            // actioned state; the reason simply catches up on the next run.
          }
          load(month, centreUuid)
        }}
      />
    </Box>
  )
}

export default MyDay
